import { initContract } from '@ts-rest/core';
import { createApi, createQueryHooks } from './ts-rest';
import type { CopilotMessage, ServerSideCopilotToolPayload } from '@customerio/hydra-copilot';
import type { RawAccount, RawUser, SearchResult, Workspace } from '../auth/types';
import type { AIContext, AIContextOverrides } from '@/pages/workspaces/settings/ai-business-profile/types';
import { crossAppCache } from '@/utils/cross-app-cache';
import type { BackfillType } from '@/features/tasks';

/**
 * Common/Shared Types
 */

export interface ServicesApiPaginationMeta {
  page: number;
  size: number;
  total: number;
}

export interface ServicesApiErrorSource {
  pointer: string;
}

export interface ServicesApiErrorDetail {
  code?: string;
  detail: string;
  source?: ServicesApiErrorSource;
  status: string;
  meta?: Record<string, unknown>;
}

export interface ServicesApiErrorResponse {
  errors: ServicesApiErrorDetail[];
}

export interface ServicesApiCustomer {
  /** Internal CIO id — always present */
  cio_id: string;
  /** External customer id (user-assigned) */
  id?: string;
  /** Freeform attributes bag — email, created_at, first_name, last_name, etc. live here */
  attributes: Record<string, unknown>;
  identifiers: {
    cio_id: string;
    id?: string;
    email?: string;
  };
  last_visited?: number;
  unsubscribed?: boolean;
}

interface ServicesApiGetPeopleResponse {
  customers: ServicesApiCustomer[];
  meta?: {
    message: string;
    pagination: {
      total: number;
      page: number;
      size: number;
    };
  };
}

interface ServicesApiGetPeopleQueryParams {
  /** When false, sort descending (newest first). */
  asc?: boolean;
  /** Email filter; API supports wildcards (e.g. `cool*`). */
  email?: string;
  page?: number;
}

/**
 * Account API Types
 */

interface ServicesApiAccountResponse {
  account: RawAccount;
  environments: Workspace[];
  user: RawUser;
  session_expires_at: number;
}

interface ServicesApiGetAccountByIdPathParams {
  accountId: number;
}

interface ServicesApiGetAccountByEnvironmentIdPathParams {
  environmentId: number;
}

interface ServicesApiSetSupportAccessPathParams {
  accountId: number;
}

interface ServicesApiGetEnvironmentPathParams {
  environmentId: number;
}

export interface ServicesApiGetEnvironmentResponse {
  environment: {
    id: string;
    account_id: string;
    workspace_id: string;
    name: string;
    delivery_settings: string;
    delivery_email: string;
    user_ids: string[];
    user_roles: Record<string, string>;
    sent_attributes: string[];
    more_attributes: boolean;
    device_attributes: string[];
    more_device_attributes: boolean;
    sent_events: string[];
    more_events: boolean;
    enabled_actions: string[];
    hex_code: string;
    push_providers: string[];
    signing_secret: string;
    unsent_drafts: boolean;
    identifier_type: string;
    type: string;
    identifiers: { cio_id: number; id: number; email: number };
    stop_processing: boolean;
    metric_denominator: string;
    email_open_tracking: boolean;
    language_attribute: string;
    identifier_settings: { identify_by_id: boolean };
    identifiers_migration_in_progress: '' | 'multi_id' | 'id_only' | 'id_mutable' | 'unknown';
    shard: string;
    subscription_center_enabled: boolean;
    max_allowed_object_types: number;
    default_template_engine: number;
    can_update_default_template_engine: boolean;
    default_preprocess_css: boolean;
    is_sample_workspace: boolean;
    example_short_link_sms: string;
    created_at: number;
    human_metrics_only: boolean;
    identity_ids: string[];
    [key: string]: unknown;
  };
  identities: ServicesApiGetIdentitiesResponse['identities'];
  object_types: unknown[] | null;
}

interface ServicesApiSetSupportAccessBody {
  Account: {
    access_granted_until: number;
  };
}

interface ServicesApiSubmitSupportRequestPathParams {
  accountId: number;
}

interface ServicesApiSubmitSupportRequestBody {
  comment: string;
  goal: string;
  video?: string;
  replyTo: string;
  consent_to_access: boolean;
  browser: string;
  url: string;
  windowDimensions: string;
  version?: string;
}

interface ServicesApiSubmitPremiumContactBody {
  message: string;
  replyTo: string;
  browser: string;
  url: string;
  windowDimensions: string;
  consent_to_access: boolean;
  source?: string;
  features?: string;
}

interface ServicesApiNullUnixTimestamp {
  Timestamp: number;
  Valid: boolean;
}

interface ServicesApiBuilderCreditGrant {
  id: number;
  credits_remaining: number;
  credits_original: number;
  expires_at: ServicesApiNullUnixTimestamp;
}

interface ServicesApiGetBuilderCreditsResponse {
  grants: ServicesApiBuilderCreditGrant[];
}

interface ServicesApiVerifyAccountStatusResult {
  account_id: number;
  decision: 'approve' | 'deny' | 'escalate';
  data: {
    pp_word_count: number;
    pp_contains_placeholder: boolean;
    tos_contains_placeholder: boolean;
    opt_in_form_exists: boolean;
    aup_risk_level: 'low' | 'medium' | 'high';
    aup_flags: string[];
    aup_violation_excerpts: Array<{ flag: string; excerpt: string; url: string }>;
    domain_authenticated: boolean;
    ehawk_score: number;
    is_free_email_provider: boolean;
    source_channel: string;
    website_scrape_success: boolean;
    pp_scrape_success: boolean;
    tos_scrape_success: boolean;
    decision_reason: string;
  };
  decided_at: string;
}

interface ServicesApiVerifyAccountStatusResponse {
  status: 'running' | 'completed' | 'failed';
  result?: ServicesApiVerifyAccountStatusResult;
  error?: string;
}

/**
 * Session Metadata API Types
 */

interface ServicesApiSessionMetadata {
  id: number;
  email: string;
  name: string;
  expires_at: number;
  workspace_ids: number[];
}

type ServicesApiGetSessionMetadataResponse = ServicesApiSessionMetadata[];

/**
 * CLI Login API Types
 */

interface ServicesApiLoginCliBody {
  client_name: string;
}

interface ServicesApiLoginCliResponse {
  token: string;
  expires_at?: number;
  service_account_id: number;
  account_id: number;
}

/**
 * CLI → Web handoff: exchange the short-lived JWT minted by `cio auth login`
 * for a regular browser session.
 */
interface ServicesApiLoginCliLinkExchangeBody {
  handoff_token: string;
}

interface ServicesApiLoginCliLinkExchangeResponse {
  redirect_to: string;
}

/**
 * Copilot API Types
 */

interface ServicesApiCreateCopilotSessionPathParams {
  accountId: number;
}

interface ServicesApiCreateCopilotSessionResponse {
  session_id: string;
  tools: ServerSideCopilotToolPayload[];
}

interface ServicesApiDeleteCopilotSessionPathParams {
  accountId: number;
  sessionId: number;
}

interface ServicesApiGetCopilotSessionPathParams {
  accountId: number;
  sessionId: number;
}

interface ServicesApiGetCopilotSessionResponse {
  name: string;
  messages: CopilotMessage[];
  tools: ServerSideCopilotToolPayload[];
}

interface ServicesApiGetCopilotFileUploadSignaturePathParams {
  accountId: number;
  sessionId: string;
}

interface ServicesApiGetCopilotFileUploadSignatureBody {
  size: string;
  mime_type: string;
}

interface ServicesApiGetCopilotFileUploadSignatureResponse {
  filename: string;
  signature: {
    fields: {
      'content-type': string;
      key: string;
      policy: string;
      success_action_status: string;
      'x-goog-algorithm': string;
      'x-goog-credential': string;
      'x-goog-date': string;
      'x-goog-meta-account-id': string;
      'x-goog-meta-session-id': string;
      'x-goog-signature': string;
    };
    url: string;
  };
}

interface ServicesApiGetCopilotFileDownloadUrlPathParams {
  accountId: number;
  sessionId: string;
  filename: string;
}

interface ServicesApiGetCopilotFileDownloadUrlResponse {
  url: string;
}

/**
 * Copilot Virtual Files API Types
 */

interface ServicesApiCopilotVirtualFile {
  path: string;
  content: string;
  created_at: string;
  updated_at: string;
}

export interface ServicesApiCopilotVirtualFileSummary {
  path: string;
  created_at: string;
  updated_at: string;
}

interface ServicesApiGetCopilotVirtualFilePathParams {
  accountId: number;
  filePath: string;
}

interface ServicesApiGetCopilotVirtualFileResponse {
  file: ServicesApiCopilotVirtualFile;
}

interface ServicesApiListCopilotFilesPathParams {
  accountId: number;
}

interface ServicesApiListCopilotFilesResponse {
  files: ServicesApiCopilotVirtualFileSummary[];
}

interface ServicesApiUpdateCopilotFilePathParams {
  accountId: number;
  filePath: string;
}

interface ServicesApiUpdateCopilotFileBody {
  content: string;
}

interface ServicesApiUpdateCopilotFileResponse {
  file: ServicesApiCopilotVirtualFile;
}

interface ServicesApiDeleteCopilotFilePathParams {
  accountId: number;
  filePath: string;
}

interface ServicesApiUpdateCopilotSessionPathParams {
  accountId: number;
  sessionId: number;
}

interface ServicesApiUpdateCopilotSessionBody {
  name: string;
}

interface ServicesApiGetCopilotSessionsPathParams {
  accountId: number;
}

interface ServicesApiGetCopilotSessionsQueryParams {
  page?: number;
}

interface ServicesApiCopilotSessionSummary {
  id: number;
  name: string;
  created_at: number;
  updated_at: number;
}

interface ServicesApiGetCopilotSessionsResponse {
  meta: {
    pagination: ServicesApiPaginationMeta;
  };
  sessions: ServicesApiCopilotSessionSummary[];
}

interface ServicesApiRecordCopilotFeedbackPathParams {
  accountId: number;
  sessionId: number;
  logId: string;
}

interface ServicesApiRecordCopilotFeedbackBody {
  thumbs_up?: boolean;
  thumbs_down?: boolean;
  feedback_reason?: string;
}

interface ServicesApiRecordCopilotFeedbackResponse {
  success: boolean;
}

interface ServicesApiGetAIContextOverridesPathParams {
  workspaceId: number;
}

interface ServicesApiGetAIContextOverridesResponse {
  ai_context?: AIContext;
  overrides?: AIContextOverrides;
}

interface ServicesApiUpdateAIContextOverridesPathParams {
  workspaceId: number;
}

interface ServicesApiUpdateAIContextOverridesBody {
  overrides: AIContextOverrides;
}

interface ServicesApiUpdateAIContextOverridesResponse {
  ai_context?: AIContext;
  overrides?: AIContextOverrides;
}

/**
 * Copilot Cron (Routines) API Types
 */

export interface ServicesApiCopilotCron {
  id: number;
  name: string;
  schedule: string;
  timezone: string;
  prompt: string;
  paused: boolean;
  next_run_at?: string;
  last_run_at?: string;
  last_status?: string;
  last_error?: string;
  created_at: string;
  updated_at: string;
}

interface ServicesApiListCopilotCronsPathParams {
  environment_id: number;
}

interface ServicesApiListCopilotCronsResponse {
  routines: ServicesApiCopilotCron[];
}

interface ServicesApiGetCopilotCronPathParams {
  environment_id: number;
  cron_id: number;
}

interface ServicesApiGetCopilotCronResponse {
  routine: ServicesApiCopilotCron;
}

interface ServicesApiUpdateCopilotCronPathParams {
  environment_id: number;
  cron_id: number;
}

interface ServicesApiUpdateCopilotCronBody {
  name?: string;
  prompt?: string;
  schedule?: string;
  paused?: boolean;
}

interface ServicesApiUpdateCopilotCronResponse {
  routine: ServicesApiCopilotCron;
}

interface ServicesApiDeleteCopilotCronPathParams {
  environment_id: number;
  cron_id: number;
}

/**
 * Workramp API Types
 */

interface ServicesApiGetWorkrampAuthUrlPathParams {
  accountId: number;
  userId: number;
}

interface ServicesApiGetWorkrampAuthUrlQuery {
  dest: string;
}

interface ServicesApiGetWorkrampAuthUrlResponse {
  url: string;
}

/**
 * Feedback API Types
 */

interface ServicesApiSubmitFeedbackPathParams {
  accountId: number;
}

interface ServicesApiSubmitFeedbackBody {
  replyTo: string;
  source: string;
  accountId: number;
  accountEmail?: string;
  userFirstName: string;
  userLastName: string;
  browser: string;
  url: string;
  windowDimensions: string;
  criticism: string;
  praise: string;
}

/**
 * OAuth2 MCP API Types
 */

interface ServicesApiOAuthMcpResponse {
  location: string;
}

/**
 * Zendesk Chat API Types
 */

interface ServicesApiGetZendeskChatAccessTokenPathParams {
  accountId: number;
  userId: number;
}

interface ServicesApiGetZendeskChatAccessTokenResponse {
  token: string;
  is_handover_allowed: boolean;
  zendesk_chat_enabled: boolean;
  token_lifetime_minutes: number;
}

interface ServicesApiRefreshZendeskChatTokenPathParams {
  accountId: number;
  userId: number;
}

interface ServicesApiRefreshZendeskChatTokenResponse {
  token: string;
}

/**
 * Tracking API Types
 */

interface ServicesApiTrackEventPathParams {
  workspaceId: number;
}

interface ServicesApiTrackEventBody {
  properties: Record<string, unknown>;
  event: string;
}

/**
 * Audit Logs API Types
 */

interface ServicesApiGetAllUsersPathParams {
  account_id: number;
}

interface ServicesApiAuditLogUser {
  user_id: number;
  email: string;
  first_name: string;
  last_name: string;
  created_at: number;
  updated_at: number;
  deleted: boolean;
}

interface ServicesApiAuditLogUserSummary {
  user_id: number;
  email: string;
  deleted: boolean;
}

interface ServicesApiGetAllUsersResponse {
  users: ServicesApiAuditLogUser[];
}

interface ServicesApiAuditLogsQueryParams {
  version?: number;
  limit?: number;
  sort_order?: 'asc' | 'desc';
  cursor?: string;
  user_id?: number;
  ip_addr?: string;
  object_type?: string;
  object_id?: string;
  action?: string;
  attribute_name?: string;
  start_time?: string;
  end_time?: string;
  /** Filter CampaignAction logs by campaign ID in payload. Requires object_type=CampaignAction. */
  campaign_id?: number;
  /** Filter Campaign logs by campaign type. Requires object_type=Campaign.
   * Values: 'triggered_broadcast' (API-Triggered Broadcasts only), 'exclude_triggered_broadcast' (all except API-Triggered) */
  campaign_type?: string;
}

export interface ServicesApiAuditLog {
  id: number;
  timestamp: number;
  account_id: number;
  user_id: number;
  service_account_id?: number;
  ip_addr: string;
  object_type: string;
  object_id: string;
  version: number;
  action: string;
  data: unknown;
  meta?: unknown;
  changed_from?: Record<string, unknown> | null;
  changed_to?: Record<string, unknown> | null;
}

interface ServicesApiAuditLogServiceAccount {
  service_account_id: number;
  name: string;
  account_type: 'user' | 'agent' | 'system' | '';
  deleted?: boolean;
}

interface ServicesApiAuditLogsPagination {
  limit: number;
  sort_order: string;
  next_cursor: number;
}

interface ServicesApiGetAccountAuditLogsPathParams {
  account_id: number;
}

interface ServicesApiGetAuditLogsResponse {
  data: ServicesApiAuditLog[];
  pagination: ServicesApiAuditLogsPagination;
  users: ServicesApiAuditLogUserSummary[];
  service_accounts?: ServicesApiAuditLogServiceAccount[];
}

interface ServicesApiGetWorkspaceAuditLogsPathParams {
  environment_id: number;
}

interface ServicesApiGetAuditLogByIdQueryParams {
  version?: number;
}

interface ServicesApiGetAccountAuditLogByIdPathParams {
  account_id: number;
  id: number;
}

interface ServicesApiGetAuditLogByIdResponse {
  data: ServicesApiAuditLog;
  user?: {
    user_id: number;
    email: string;
    deleted?: boolean;
  };
  service_account?: ServicesApiAuditLogServiceAccount;
}

interface ServicesApiGetWorkspaceAuditLogByIdPathParams {
  environment_id: number;
  id: number;
}

/**
 * Audit Logs Export API Types
 */

interface ServicesApiExportAuditLogsBody {
  start_time: string; // Required, RFC3339 format
  end_time: string; // Required, RFC3339 format
  object_type?: string; // Optional filter
  environment_id?: number; // Required for account-level exports (workspace to create export in)
}

interface ServicesApiExportAuditLogsResponse {
  export_id: string;
}

interface ServicesApiExportAccountAuditLogsPathParams {
  account_id: number;
}

interface ServicesApiExportWorkspaceAuditLogsPathParams {
  environment_id: number;
}

/**
 * Search API Types
 */

interface ServicesApiSearchPathParams {
  workspaceId: number;
}

export type SearchFilters = Record<string, string[]>;

interface ServicesApiSearchQueryParams {
  q: string;
  limit?: number;
  test?: number;
  /** URL-safe base64-encoded JSON object mapping filter field names to arrays of allowed values. Use `encodeSearchFilters` to produce this value. */
  filters?: string;
  order_by?: string;
}

interface ServicesApiSearchResponse {
  results: SearchResult[] | null;
  query: string;
  limit: number;
  filters?: SearchFilters;
  order_by?: string;
}

/**
 * Encodes a search filters object into a URL-safe base64 string
 * for the `/search` endpoint's `filters` query parameter.
 *
 * @example
 * encodeSearchFilters({ kind: ['segment', 'campaign'] })
 * // => 'eyJraW5kIjpbInNlZ21lbnQiLCJjYW1wYWlnbiJdfQ'
 */
export function encodeSearchFilters(filters: SearchFilters): string {
  return btoa(JSON.stringify(filters)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Campaigns API Types
 */

interface ServicesApiGetCampaignsPathParams {
  environment_id: number;
}

interface ServicesApiGetCampaignsQueryParams {
  // Pagination (0-indexed)
  page?: number;
  size?: number;
  // Filtering
  type?: string;
  'searchType[]'?: string[];
  searchString?: string;
  search?: string;
  state?: string;
  sample?: boolean;
  archived?: boolean;
  'tagIds[]'?: string[];
  'searchTagIds[]'?: string[];
  subscriptionTopicId?: number;
  sendToUnsubscribed?: string;
  'objectTypeIds[]'?: string[];
  // Sorting
  sort?: string;
  asc?: boolean;
  // Metrics
  version?: string;
  start?: number;
  end?: number;
  res?: string;
  tz?: string;
  // Response shaping
  excludeActions?: boolean;
  excludeMetrics?: boolean;
  excludeTags?: boolean;
  excludeSegmentMatchCount?: boolean;
}

export interface ServicesApiCampaign {
  id: string;
  account_id: number;
  name: string;
  type: string;
  description: string;
  state: string;
  /**
   * Wire is `string[]`. The db layer's `Tag.id` is also `string` (Tag's
   * Zod schema coerces the wire-numeric id to a string at parse, so
   * cache keys, FK arrays, and hooks all agree on `string`).
   */
  tag_ids: string[];
  version: number;
  created: number;
  updated: number;
  deleted: boolean;
  created_by_user: ServicesApiUserMinimal | null;
  updated_by_user: ServicesApiUserMinimal | null;
  first_started: number | null;
  send_to_unsubscribed: boolean;
  use_message_limits: boolean;
  use_workspace_message_limits_retry: boolean;
  message_limits_retry_window_secs: number;
  subscription_topic_id?: string;
  restart_mode: string;
  restart_min_interval: number;
  unsent_drafts: boolean;
  sent: number | null;
  total_sent: number | null;
  delivered: number | null;
  channel_metrics: Record<string, unknown> | null;
  workflow_action_ids: string[];
  // V2 fields
  edges: Array<{ from: string; to: string; type: string; index?: number }>;
  conversion: boolean | null;
  conversion_type: string;
  conversion_action: string;
  conversion_window: number;
  conversion_segment_id: string | null;
  conversion_event_name: string | null;
  conversion_event_filters?: unknown;
  event: string;
  event_type: string;
  anchors: string;
  filters: string;
  trigger?: unknown;
  match_count: number;
  match_count_finished: boolean;
  backing_segment_id: string | null;
  recipients_filter?: string;
  attribute_filters: unknown;
  exit_on_trigger_or_filter_not_matched: boolean;
  exit_on_conversion_matched: boolean;
  most_recent_trigger_id?: string;
  trigger_count?: number;
  queued_triggers?: number;
  date_triggered_frequency: string;
  date_triggered_start_time: string;
  date_triggered_lead_days: number;
  date_triggered_attribute: string;
  date_triggered_zone?: string;
  date_triggered_fallback_zone?: string;
  sample: boolean;
  // V3 fields
  journey_map?: unknown;
}

export interface ServicesApiGetCampaignsResponse {
  campaigns: ServicesApiCampaign[];
  actions?: ServicesApiAction[];
  tags?: ServicesApiTag[];
  subscription_topics?: ServicesApiSubscriptionTopic[];
  meta?: {
    pagination?: ServicesApiPaginationMeta;
    totals?: {
      has_campaigns: boolean;
      has_legacy_segment_triggered_campaigns?: boolean;
      campaigns: number;
      archived: number;
      all_time?: Record<string, number>;
    };
  };
}

/**
 * Segments API Types
 */

interface ServicesApiGetSegmentsPathParams {
  environment_id: number;
}

interface ServicesApiGetSegmentsQueryParams {
  // Pagination (0-indexed, only used when size is set)
  page?: number;
  size?: number;
  // Filtering
  search?: string;
  type?: string;
  archived?: boolean;
  usage?: string; // 'in_use' | 'not_in_use' | 'in_sent_newsletters' | 'all'
  audience_status?: string;
  audience_type?: string;
  searchTagIds?: string;
  objectTypeIds?: string;
  // Short response (returns SegmentDesc instead of full Segment)
  short?: string;
}

export interface ServicesApiUserMinimal {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
}

export interface ServicesApiSegment {
  id: number;
  environment_id: number;
  type: string;
  name: string;
  description: string;
  conditions?: unknown;
  timestamp?: number | null;
  progress?: number | null;
  state: string;
  system?: string;
  created_at: number;
  updated_at: number;
  created_by_user?: ServicesApiUserMinimal | null;
  updated_by_user?: ServicesApiUserMinimal | null;
  meta?: string | null;
  in_use_active?: boolean;
  in_use_archived?: boolean;
  archived?: boolean;
  audience_ids?: string[];
  tag_ids?: string[];
  auto_archived_at?: number | null;
  linked_form_type?: string;
  linked_form_id?: string;
  has_relationship_conditions?: boolean;
  object_type_ids?: number[];
}

export interface ServicesApiGetSegmentsResponse {
  segments: ServicesApiSegment[];
  tags?: Array<{ id: string; name: string }>;
  meta?: { system: string };
}

/**
 * Newsletters API Types
 */

interface ServicesApiGetNewslettersPathParams {
  environment_id: number;
}

interface ServicesApiGetNewslettersQueryParams {
  page?: number;
  searchString?: string;
  archived?: boolean;
}

export interface ServicesApiNewsletter {
  id: number;
  name: string;
  state: string;
  channel?: string;
  sending?: boolean;
  sent_at?: number | null;
  cancelled_at?: number | null;
  scheduled_at?: number | null;
  is_paused?: boolean;
  total_sent?: number;
  total_delivered?: number;
}

export interface ServicesApiGetNewslettersResponse {
  newsletters: ServicesApiNewsletter[];
  meta?: {
    pagination?: ServicesApiPaginationMeta;
  };
}

/**
 * Transactional Messages API Types
 */

interface ServicesApiGetTransactionalMessagesPathParams {
  environment_id: number;
}

interface ServicesApiGetTransactionalMessagesQueryParams {
  searchString?: string;
}

export interface ServicesApiTransactionalMessage {
  id: number;
  name: string;
  type?: string;
  description?: string;
  state?: string;
  trigger_name?: string;
  created?: number;
  updated?: number;
}

export interface ServicesApiGetTransactionalMessagesResponse {
  transactional_messages: ServicesApiTransactionalMessage[];
}

interface ServicesApiTransactionalMessageMetric {
  transactional_message_id: number;
  total_sent: number;
}

interface ServicesApiGetTransactionalMessageMetricsResponse {
  metric: ServicesApiTransactionalMessageMetric;
}

/**
 * Delivery Filters API Types
 */
interface ServicesApiGetDeliveryFiltersPathParams {
  environment_id: number;
}

interface ServicesApiGetDeliveryFiltersQueryParams {
  type?: 'campaigns' | 'newsletters' | 'transactional_messages' | 'messages';
}

interface ServicesApiFilterMessage {
  action_id: string;
  campaign_id: string;
  newsletter_id: string;
  output_id: string;
  subject: string;
  type: string; // Channel: 'email', 'twilio', 'webhook', 'in_app', 'push', 'slack'
  can_segment_on_variation?: boolean;
  template_id?: string;
  transactional_message_id?: string;
  in_app_broadcast_id?: string;
  variation?: number | null;
  language?: string | null;
}

interface ServicesApiGetDeliveryFiltersResponse {
  campaigns?: Array<{ id: string; name: string; type: string; state: string; first_started: number | null }>;
  newsletters?: Array<{ id: string; name: string }>;
  messages?: ServicesApiFilterMessage[];
  segmentable?: ServicesApiFilterMessage[];
  transactional_messages?: Array<{ id: string; name: string; type: string }>;
  branch_messages?: ServicesApiFilterMessage[];
  in_app_broadcasts?: Array<{ id: string; name: string; output_id: string }>;
}

/**
 * Backfills (Tasks) API Types
 */

export type ServicesApiBackfillState = 'Queued' | 'InProgress' | 'Failure' | 'Success' | 'Retrying' | 'Canceled';

export type ServicesApiBackfillPriority =
  | 'Priority'
  | 'Default'
  | 'Background'
  | 'Import'
  | 'SendNewsletter'
  | 'ProfileExport'
  | 'DeliveriesExport'
  | 'SQL_Import'
  | 'RunAlias';

export interface ServicesApiBackfill {
  environment_id: number;
  id: number;
  object_id?: number;
  object_name?: string;
  object_version?: number;
  object_deleted?: boolean;
  related_object_id?: number;
  template_ids?: string[];
  delivery_ids?: string[];
  priority: ServicesApiBackfillPriority;
  type: string;
  state: ServicesApiBackfillState;
  status: string;
  created: number;
  last_updated: number;
  started?: number;
  completed?: number;
  error?: string;
}

interface ServicesApiGetBackfillsPathParams {
  environment_id: number;
}
interface ServicesApiGetBackfillsQueryParams {
  continuation?: string;
  state?: BackfillState;
  type?: BackfillType;
}

export const backfillStates = ['Queued', 'InProgress', 'Failure', 'Success', 'Retrying', 'Canceled'] as const;
export type BackfillState = (typeof backfillStates)[number];

interface ServicesApiGetBackfillsResponse {
  backfills: ServicesApiBackfill[];
  meta: {
    pagination: {
      page: number;
      size: number;
    };
    continuation: string;
  };
}

/**
 * Environment Health API Types
 */

interface ServicesApiGetEnvHealthPathParams {
  environment_id: number;
}

export interface ServicesApiGetEnvHealthQueryParams {
  simulate?: 'healthy' | 'busy' | 'degraded';
}

export interface ServicesApiEnvHealthProcessingCategory {
  name: string;
  pending: number;
  delay_seconds?: number;
}

export interface ServicesApiEnvHealthTaskDetail {
  type: string;
  object_name?: string;
  started?: number;
  id: number;
  state: ServicesApiBackfillState;
  priority: string;
}

export interface ServicesApiEnvHealthTaskSummary {
  type: string;
  object_name?: string;
  error?: string;
  failed_at?: number;
}

interface ServicesApiEnvHealthEventNameCount {
  name: string;
  count: number;
}

export interface ServicesApiEnvHealthDailyStats {
  tasks_failed_today: number;
  failed_events_today: Record<string, number> | null;
  throughput: Record<string, ServicesApiEnvHealthThroughputStat>;
  processed_today: Record<string, ServicesApiEnvHealthCountStat>;
}

export interface ServicesApiEnvHealthThroughputStat {
  current: number;
  prev_day: number;
}

export interface ServicesApiEnvHealthCountStat {
  current: number;
  prev_day: number;
}

interface ServicesApiEnvHealthDelayHistoryPoint {
  t: number;
  v: number;
}

interface ServicesApiEnvHealthDelayHistoryResponse {
  ingress: ServicesApiEnvHealthDelayHistoryPoint[] | null;
  egress: ServicesApiEnvHealthDelayHistoryPoint[] | null;
}

interface ServicesApiEnvHealthTopEventsResponse {
  top_events: ServicesApiEnvHealthEventNameCount[];
}

export interface ServicesApiEnvHealthSegmentStats {
  unused_count: number;
  newsletter_only_count: number;
  total_count: number;
}

interface ServicesApiEnvHealthSegmentsResponse {
  segments: ServicesApiEnvHealthSegmentStats;
}

interface ServicesApiEnvHealthObjectTypeStat {
  id: number;
  name: string;
  object_count: number;
  relationship_count: number;
  in_use: boolean;
}

interface ServicesApiEnvHealthObjectsResponse {
  object_type_count: number;
  total_objects: number;
  total_relationships: number;
  types: ServicesApiEnvHealthObjectTypeStat[];
  processed_today: ServicesApiEnvHealthCountStat;
  related_updates: ServicesApiEnvHealthCountStat;
}

export interface ServicesApiEnvHealthStaleCampaign {
  id: number;
  name: string;
  first_started: number;
  last_sent_at?: number;
}

export interface ServicesApiEnvHealthCampaignsResponse {
  active_count: number;
  stale_count: number;
  stale: ServicesApiEnvHealthStaleCampaign[];
  processed_today: number;
}

interface ServicesApiEnvHealthApiTriggeredBroadcastsResponse {
  pending_count: number;
  with_errors_count: number;
  processed_today: number;
  recipients_today_total: number;
}

export interface ServicesApiEnvHealthPushMetricTrend {
  current: string;
  previous: string;
  direction: 'improving' | 'stable' | 'declining';
  active: boolean;
}

export interface ServicesApiEnvHealthPushPlatformTrends {
  platform: string;
  metrics: {
    delivery_rate: ServicesApiEnvHealthPushMetricTrend;
    bounce_rate: ServicesApiEnvHealthPushMetricTrend;
    open_rate: ServicesApiEnvHealthPushMetricTrend;
  };
}

/** A single push health configuration check.
 *  Known check names: "APNs Credentials", "FCM Credentials", "Rich Push Setup".
 */
interface ServicesApiEnvHealthPushConfigItem {
  name: string;
  status: 'ok' | 'missing' | 'warning';
  detail: string;
}

interface ServicesApiEnvHealthPushResponse {
  period: string;
  configuration: {
    checks: ServicesApiEnvHealthPushConfigItem[];
  };
  platforms: ServicesApiEnvHealthPushPlatformTrends[];
}

interface ServicesApiEnvHealthErroredConnection {
  /** Matches the identifier format from `/integrations/pipelines`, e.g. "pipelines_PipelinesDestination_2". */
  id: string;
  name: string;
  status_message: string;
}

interface ServicesApiEnvHealthPipelinesResponse {
  total_connections: number;
  active_connections: number;
  connections_with_errors: number;
  /** Unix epoch seconds; 0 when no pipeline activity has been recorded. */
  last_activity_unix: number;
  errored_connections: ServicesApiEnvHealthErroredConnection[];
}

export interface ServicesApiEnvHealthAlert {
  severity: 'caution' | 'error' | 'info';
  title: string;
  message: string;
  action_label: string;
  action_type: string;
  /**
   * Tailored prompt the dashboard passes to copilot when the user clicks the
   * "Ask agent" CTA on this alert. Maps each alert to its diagnostic playbook
   * via the `diagnosing-workspace-health` copilot skill. Optional because the
   * field is added in services#19752; older responses won't include it.
   */
  copilot_prompt?: string;
}

export interface ServicesApiEnvHealthResponse {
  health: 'healthy' | 'busy' | 'degraded';
  score: number;
  processing: {
    total_pending: number;
    drain_seconds?: number;
    segments_paused?: boolean;
    categories: ServicesApiEnvHealthProcessingCategory[];
  };
  egress: {
    total_pending: number;
    drain_seconds?: number;
    categories: ServicesApiEnvHealthProcessingCategory[];
  };
  tasks: {
    running_tasks?: ServicesApiEnvHealthTaskDetail[];
    queued_by_priority?: Record<string, number>;
    failed: ServicesApiEnvHealthTaskSummary[];
  };
  stats: ServicesApiEnvHealthDailyStats;
  alerts: ServicesApiEnvHealthAlert[];
  /**
   * Unix epoch seconds when the cached response body was computed. Render via
   * `new Date(last_updated_at * 1000)`. Optional because the field may not be
   * present during the transition window before services#19611 deploys.
   */
  last_updated_at?: number;
  /**
   * Unix epoch seconds when the backend cache will next refresh
   * (`last_updated_at + ResponseCacheTTL`). Optional for the same transition reason.
   */
  next_refresh_at?: number;
}

interface ServicesApiEnvHealthCheckResponse {
  healthy: boolean;
}

/**
 * Data Index Totals API Types
 */

export interface ServicesApiTotals {
  all: number;
  in_use: number;
}

export interface ServicesApiTotalsResponse {
  totals: ServicesApiTotals;
}

interface ServicesApiGetTotalsPathParams {
  environment_id: number;
}

/**
 * Attributes List API Types
 */

interface ServicesApiAttributeMetadataSummary {
  description?: string | null;
  privacy_level?: string;
  tag_ids?: number[];
  sources?: Array<{ id: number; name: string; type: string }>;
}

interface ServicesApiAttributeListItem {
  /** Attribute name (serialized as `id` by the backend) */
  id: string;
  last_seen: number;
  sample: Record<string, unknown>;
  object_type_id?: number;
  attribute_type?: string;
  metadata_summary: ServicesApiAttributeMetadataSummary;
}

interface ServicesApiGetAttributesQueryParams {
  page?: number;
  size?: number;
  term?: string;
  in_use_only?: boolean;
  tag_ids?: string;
  source_ids?: string;
}

interface ServicesApiGetAttributesResponse {
  attributes: ServicesApiAttributeListItem[];
  tags?: Array<{ id: number; name: string }>;
  meta?: {
    pagination: ServicesApiPaginationMeta;
  };
}

/**
 * Event Names List API Types
 */

interface ServicesApiEventMetadataSummary {
  last_seen_as?: string | null;
  description?: string | null;
  tag_ids?: number[];
  sources?: Array<{ id: number; name: string; type: string }>;
}

interface ServicesApiEventListItem {
  /** Event name (serialized as `id` by the backend) */
  id: string;
  last_seen: number;
  sample: Record<string, unknown>;
  metadata?: ServicesApiEventMetadataSummary;
}

interface ServicesApiGetEventNamesQueryParams {
  page?: number;
  size?: number;
  term?: string;
  in_use_only?: boolean;
  tag_ids?: string;
  source_ids?: string;
}

interface ServicesApiGetEventNamesResponse {
  events: ServicesApiEventListItem[];
  tags?: Array<{ id: number; name: string }>;
  meta?: {
    pagination: ServicesApiPaginationMeta;
  };
}

/**
 * Object Types API Types
 */

export interface ServicesApiObjectTypeUsage {
  segment_count: number;
  campaign_count: number;
  newsletter_count: number;
  apitb_count: number;
  snippet_count: number;
  transactional_count: number;
}

export interface ServicesApiObjectType {
  id: number;
  name: string;
  singular_name: string;
  slug: string;
  singular_slug: string;
  color: string;
  icon?: string;
  enabled: boolean;
  has_non_ui_objects: boolean;
  sent_attributes?: string[];
  more_attributes?: boolean;
  sent_rel_attributes?: string[];
  more_rel_attributes?: boolean;
  object_count: number;
  usage: ServicesApiObjectTypeUsage;
}

// Referenced by the ts-rest contract a few thousand lines below and
// the response is the canonical list shape; future entity callers
// (object-type cluster migration) will import this type directly. Keep
// it exported for symmetry with the surrounding `ServicesApiGet*Response`
// types.
// fallow-ignore-next-line unused-type
export interface ServicesApiGetObjectTypesResponse {
  object_types: ServicesApiObjectType[];
}

/**
 * Subscription Topics API Types
 */

export interface ServicesApiSubscriptionTopicUsage {
  campaign_count?: number;
  newsletter_count?: number;
}

export interface ServicesApiSubscriptionTopic {
  /**
   * Wire is `int` (Go `model.SubscriptionTopic.ID`). The db-layer
   * `SubscriptionTopic.id` is `string` — coerced at the parse boundary
   * for the same reason as `ServicesApiTag.id`. `Campaign.subscription_topic_id`
   * is also `string` on the wire, so cache keys and FK-follow line up.
   */
  id: number;
  name: string;
  description: string;
  created: number;
  updated: number;
  opt_in: boolean;
  usage?: ServicesApiSubscriptionTopicUsage;
}

interface ServicesApiGetSubscriptionTopicsResponse {
  subscription_topics: ServicesApiSubscriptionTopic[];
}

interface ServicesApiSubscriptionTopicResponse {
  subscription_topic: ServicesApiSubscriptionTopic;
}

interface ServicesApiSubscriptionTopicWriteBody {
  subscription_topic: {
    name: string;
    description?: string;
    id?: number;
    opt_in?: boolean;
  };
}

/**
 * Custom Forms API Types
 */

interface ServicesApiCustomForm {
  id: string;
  form_type: string;
  form_id: string;
  api_form_id: string;
  html_form_url: string;
  html_form_inputs: string[];
  html_fingerprint: string;
  name: string;
  status: string;
  error: string;
  created_at: number;
  updated_at: number;
  last_activity_at: number;
}

interface ServicesApiGetCustomFormsResponse {
  custom_forms: ServicesApiCustomForm[];
}

/**
 * Facebook Lead Ads Forms API Types
 */

interface ServicesApiFacebookLeadAdsFormAttributeMap {
  facebook_field: string;
  cio_attribute: string;
}

interface ServicesApiFacebookLeadAdsForm {
  id: string;
  status: string;
  facebook_form_id: string;
  facebook_form_name: string;
  created_at: number;
  updated_at: number;
  last_activity_at: number;
  attr_maps: ServicesApiFacebookLeadAdsFormAttributeMap[];
  extra_attrs: Record<string, string>;
}

interface ServicesApiGetFacebookLeadAdsFormsResponse {
  facebook_lead_ads_forms: ServicesApiFacebookLeadAdsForm[];
}

/**
 * Domains API Types
 */

export interface ServicesApiDomainDnsRecord {
  domain: string;
  type: string;
  value: string;
  name: string;
}

export interface ServicesApiDomain {
  /** Wire encodes the int id as a JSON string via `json:"id,string"`. */
  id: string;
  domain: string;
  dns_records: ServicesApiDomainDnsRecord[];
  cname: string;
  // `verified` and most `verified_*` flags are `*bool` in Go
  // (`model.Domain`), so the wire emits `true | false | null`.
  // Existing consumer predicates `(d) => d.verified` quietly treated
  // null as false; the explicit `=== true` form forces the call site
  // to acknowledge the three-state shape.
  verified: boolean | null;
  verified_spf: boolean | null;
  verified_spf_manually: boolean | null;
  verified_dkim: boolean | null;
  verified_cname: boolean | null;
  verified_domain: boolean | null;
  /** `bool` (not pointer) in Go — never null. */
  verified_https: boolean;
  verified_hsts: boolean | null;
  verified_http: boolean | null;
  verified_dmarc: boolean | null;
  platform: string;
  created_at: number;
  updated_at: number;
  parent_domain: string;
  ip_pool: string;
  auto_tls_domain_id?: number;
}

interface ServicesApiAutoTLSDomainDnsRecord {
  domain: string;
  type: string;
  value: string;
  name: string;
}

interface ServicesApiAutoTLSDomainConfig {
  dns_records: ServicesApiAutoTLSDomainDnsRecord[];
  enabled_for_link_shortening: boolean;
}

interface ServicesApiAutoTLSDomain {
  id: number;
  domain: string;
  config: ServicesApiAutoTLSDomainConfig;
  purpose: string;
  state: string;
  last_error: string;
  last_error_time: number;
  created_at: number;
  updated_at: number;
  created_by_user: ServicesApiUserMinimal | null;
  updated_by_user: ServicesApiUserMinimal | null;
}

interface ServicesApiGetDomainsResponse {
  domains: ServicesApiDomain[];
  auto_tls_domains?: ServicesApiAutoTLSDomain[];
}

interface ServicesApiAutoTLSDomainResponse {
  auto_tls_domain: ServicesApiAutoTLSDomain;
}

interface ServicesApiDomainResponse {
  domain: ServicesApiDomain;
  auto_tls_domain?: ServicesApiAutoTLSDomain;
}

interface ServicesApiDomainCreateResponse {
  domain: ServicesApiDomain;
  dns_provider?: string;
  domain_connect_url?: string;
  auto_tls_domain?: ServicesApiAutoTLSDomain;
}

interface ServicesApiDomainUpdateResponse {
  domain: ServicesApiDomain;
  auto_tls_domain?: ServicesApiAutoTLSDomain;
  warnings?: Record<string, string[]>;
}

/**
 * Tags API Types
 */

export interface ServicesApiTag {
  /**
   * Wire is `int` (Go `model.Tag.ID`). The db-layer `Tag.id` is `string`
   * — Tag's Zod schema coerces this at parse and back at serialize so
   * cache keys, `Campaign.tag_ids` (already string-typed on the wire),
   * and FK-follow hooks all agree on `string`.
   */
  id: number;
  name: string;
  color: string;
  campaigns_used: number;
  segments_used: number;
  newsletters_used: number;
  in_app_broadcasts_used: number;
  api_triggered_used: number;
  transactional_messages_used: number;
  goals_used?: number;
  created: number;
  updated: number;
}

interface ServicesApiGetTagsResponse {
  tags: ServicesApiTag[];
}

interface ServicesApiTagResponse {
  tag: ServicesApiTag;
}

interface ServicesApiTagWriteBody {
  tag: {
    name: string;
    id?: number;
    color?: string | null;
  };
}

/**
 * Action API Types — used inside campaign + action collections.
 *
 * Mirrors `services:ui_api/model/action.go#ActionToUI` faithfully. The Go
 * type embeds many `*Action…` sub-structs (one per action variant); when an
 * embedded pointer is non-nil, its fields appear at the JSON top level. The
 * TypeScript shape below flattens those embeds into one type with everything
 * optional — only the fields relevant to a given action variant will be
 * populated on any single row.
 *
 * The list endpoint (`getCampaigns`) returns `ActionForCampaignsList`, a
 * sparse variant covering only the top-level fields plus `*ActionMultiConditional`.
 * Detail and mutation endpoints return the full `ActionToUI`. List-loaded
 * rows leave detail-only fields `undefined`; mutation responses promote
 * summary→detail in the cache via upsert.
 */

interface ServicesApiActionPosition {
  x: number;
  y: number;
  relative_to: { id: string; x: number; y: number } | null;
}

/** Mirrors `model.SplitMetricLinks` (only one field today). */
interface ServicesApiActionSplitMetricLinks {
  split_metrics: string;
}

/** Mirrors `model.TriggerElement`. */
interface ServicesApiActionTriggerElement {
  type: string;
  filter: string;
  event_name: string;
  object_type_id: number;
  within_secs: number;
  date_recurrence_type: string;
  date_attribute: string;
  offset_days: number;
  hour: number;
  minute: number;
  default_tz: string;
  use_customer_tz: boolean;
  webhook_id: string;
  form_provider: string;
  form_id: string;
}

/** Mirrors `model.LLMCall`. */
interface ServicesApiActionLLMCall {
  prompt_template_id: number;
  result_schema_template_id: number;
  model: string;
  llm_configuration_model_id: number;
}

/** Mirrors `model.LLMResultAction`. */
interface ServicesApiActionLLMResultAction {
  type: string;
}

export interface ServicesApiAction {
  // Top-level (`ActionToUI`)
  type: string;
  sub_type?: string;
  id: string;
  environment_id?: number;
  campaign_id?: string;
  updated?: number;
  /** JSON tag is `action_state` on the backend (Go field `State`). */
  action_state?: string;
  created?: number;
  name?: string;
  warnings?: string | null;
  position?: ServicesApiActionPosition | null;
  object_type_id?: number;

  // ActionSubscription
  send_to_unsubscribed?: boolean | null;
  subscription_topic_id?: number | null;

  // ActionResultAttributes
  attributes_names?: string[];
  attributes_values?: string[];
  attribute_destinations?: number[];
  attribute_fallback_values?: string[];

  // ActionPreconditions
  preconditions?: string;

  // ActionDelaySeconds
  delay?: number;

  // ActionDelayTimeWindow
  start_time?: string;
  end_time?: string;
  days?: string;
  zone?: string;
  fallback_zone?: string;

  // ActionRandomizedDelay
  min_delay?: number;
  max_delay?: number;

  // ActionTemplate (`template_id` is `int` serialized via Go's `,string` tag → arrives as a string)
  template_id?: string;
  template_type?: string;

  // ActionDelivery
  tracked?: boolean;
  has_content?: boolean;
  has_subject?: boolean;
  sending_state?: string;
  subject?: string;
  recipient?: string;
  can_convert?: boolean;
  holdout?: boolean;
  optimal_time_description?: string;
  optimal_time_start?: string;
  optimal_time_end?: string;
  optimal_time_days?: number[];

  // ActionWithMessageLimits
  use_message_limits?: boolean | null;
  override_message_limits_retry?: boolean;
  message_limits_retry_window_secs?: number;
  frequency_cap_mode?: string | null;
  frequency_cap_id?: number;

  // ActionWebhook
  request_method?: string;
  hostname?: string;
  has_url?: boolean;
  await_completion?: boolean;
  webhook_config_id?: number;

  // ActionAttribute
  attribute_name?: string;
  attribute_value?: string;
  attributes?: string;

  // ActionSplit
  action_ids?: string[];

  // ActionSplitRandom
  allocations?: number[];
  split_state?: string;
  /** epoch-seconds — `Started` field, JSON tag `started`. */
  started?: number;
  links?: ServicesApiActionSplitMetricLinks;

  // ActionWithPlatform
  send_to_platform?: string;

  // ActionConditionalWait
  exit_type?: string;

  // ActionConditional
  conditions?: string;

  // ActionConditionalEventAttr
  delay_event_attr?: string;
  delay_event_attr_offset?: number;

  // ActionMultiConditional
  multi_conditions?: string[];

  // ActionRandomCohort
  cohorts?: number[];
  cohort_names?: string[];

  // ActionStaticSegUpdate
  segment_id?: string | null;
  add_to_seg?: boolean;

  // ActionQueryCollection
  collection_id?: string | null;
  limit?: number | null;
  order_by?: string | null;
  asc?: boolean | null;
  randomize?: boolean;
  single_result?: boolean;
  query_preview_columns?: string[];
  journey_attribute_name?: string;

  // ActionCreateWebhook
  /** `ActionCreateWebhook.WebhookID` — distinct from `ActionWebhook.webhook_config_id`. */
  webhook_id?: string;

  // ActionCreateEvent
  event_name?: string;

  // ActionTrigger
  trigger_conditions?: ServicesApiActionTriggerElement[];

  // ActionLLMAction
  llm_calls?: ServicesApiActionLLMCall[];
  llm_result_action?: ServicesApiActionLLMResultAction;
}

/**
 * Shared "campaign envelope" returned by every campaign mutation and by
 * `getCampaignById`. Mirrors the backend's `CampaignModel`.
 */
export interface ServicesApiCampaignModel {
  campaign: ServicesApiCampaign;
  // Sideload arrays — the wire frequently omits whichever clusters
  // the campaign hasn't pulled in (e.g. an action-less draft has no
  // `actions`). Match runtime: `parseCampaignModel` defensively
  // `?? []`s each.
  actions?: ServicesApiAction[];
  tags?: ServicesApiTag[];
  subscription_topics?: ServicesApiSubscriptionTopic[];
}

interface ServicesApiCampaignDetailResponse extends ServicesApiCampaignModel {
  most_recent_trigger?: unknown;
}

/**
 * Body for `PUT /campaigns/:id`. The backend dispatches on `update_type` —
 * see `MIGRATION.md` "update_type dispatch (newsletter pattern)" for the
 * frontend convention (each `update_type` becomes its own exported action
 * hook). Most fields are optional and depend on the `update_type`.
 */
export interface ServicesApiCampaignUpdateBody {
  campaign: { update_type: string; [key: string]: unknown };
  action?: Partial<ServicesApiAction>;
  actions?: Array<{ type: string; [key: string]: unknown }>;
  action_ids?: string[];
  edges?: Array<{ from: string; to: string; type: string; index?: number }>;
  copy_from_env?: string;
  exit_early?: boolean;
  default_language_action_id?: string;
  updated_note_positions?: unknown[];
}

interface ServicesApiCampaignCreateBody {
  campaign: { name: string; type: string; [key: string]: unknown };
}

export interface ServicesApiActionUpdateBody {
  action: Partial<ServicesApiAction> & { id: string };
}

interface ServicesApiActionUpdateResponse {
  action: ServicesApiAction;
  actions?: ServicesApiAction[];
}

/**
 * `GET /actions/:action_id` response shape — mirrors
 * `services:ui_api/campaigns/action_types.go#actionGetResponse`. The
 * `_actions` array (note the leading underscore) carries sub-actions, e.g.
 * the variants of a split test.
 */
interface ServicesApiActionDetailResponse {
  action: ServicesApiAction;
  /**
   * Optional in the wire response — actions without sub-actions
   * (most non-split-test actions) omit the field entirely. Callers
   * must `?? []` it before iterating.
   */
  _actions?: ServicesApiAction[];
}

interface ServicesApiActionVariablesResponse {
  variables: Record<string, unknown>;
}

interface ServicesApiActionSplitResponse extends ServicesApiCampaignModel {
  meta: { action_id: string };
}

interface ServicesApiCreateSplitTestBody {
  campaign_id: string;
  type: 'randomized';
}

interface ServicesApiSelectSplitWinnerBody {
  campaign_id: string;
  winner_id: string;
}

interface ServicesApiUpdateDomainBody {
  cname: string;
}

interface ServicesApiRequestIpPoolBody {
  ip_pool: string;
  volume?: string;
  comments?: string;
}

/**
 * Releases API Types
 */

interface ServicesApiReleaseVersion {
  version: string;
  created_at: number;
}

interface ServicesApiReleaseSummaryResponse {
  hydra: ServicesApiReleaseVersion;
  journeys: ServicesApiReleaseVersion;
}

/**
 * Mail Servers API Types
 */

interface ServicesApiMailServer {
  id: string;
  address: string;
  port: number;
  authentication: 'none' | 'plain' | 'login' | 'cram_md5';
  username: string;
  password?: string;
  default_for:
    | 'none'
    | 'all'
    | 'behavioral'
    | 'transactional'
    | 'newsletter'
    | 'api-triggered'
    | 'transactional_message';
  use_api: boolean;
}

interface ServicesApiGetMailServersResponse {
  mail_servers: ServicesApiMailServer[];
}

interface ServicesApiGetMailServersPathParams {
  environment_id: number;
}

interface ServicesApiCreateMailServerBody {
  mail_server: {
    address: string;
    port: number;
    authentication: ServicesApiMailServer['authentication'];
    username: string;
    password?: string;
    default_for: ServicesApiMailServer['default_for'];
    use_api: boolean;
  };
}

interface ServicesApiCreateMailServerResponse {
  mail_server: ServicesApiMailServer;
}

interface ServicesApiCreateMailServerPathParams {
  environment_id: number;
}

interface ServicesApiDeleteMailServerPathParams {
  environment_id: number;
  id: number;
}

/**
 * Layouts API Types
 */

interface ServicesApiLayout {
  id: number;
  name: string;
}

interface ServicesApiGetLayoutsPathParams {
  environment_id: number;
}

interface ServicesApiGetLayoutsResponse {
  layouts: ServicesApiLayout[];
}

/**
 * Identities API Types
 */
export interface ServicesApiGetIdentitiesResponse {
  identities:
    | {
        address: string;
        auto_generated: boolean;
        email: string;
        environment_id: string;
        id: string;
        name: string;
        phone: string;
        template_type: 'email' | 'twilio' | 'whatsapp';
        verified: 'verified' | 'unverified' | 'undetermined';
        workspace_id: string;
      }[]
    | null;
}

/**
 * Default From Identity API Types
 */

interface ServicesApiGetDefaultFromIdentityPathParams {
  environment_id: number;
}

interface ServicesApiGetDefaultFromIdentityResponse {
  identity_id: number;
}

/**
 * Newsletter Mutations API Types
 */

interface ServicesApiCreateNewsletterPathParams {
  environment_id: number;
}

interface ServicesApiCreateNewsletterBody {
  newsletter: {
    name: string;
    send_percentage: number;
  };
}

interface ServicesApiCreateNewsletterResponse {
  newsletter: {
    id: number;
    name: string;
    channel: string;
    template_id: string;
    template_ids: string[];
    send_percentage: number;
    created: number;
  };
}

interface ServicesApiUpdateNewsletterPathParams {
  environment_id: number;
  id: number;
}

interface ServicesApiNewsletterChannelUpdate {
  newsletter: {
    update_type: 'channel';
    channel: 'email' | 'push' | 'sms' | 'in_app' | 'webhook' | 'slack';
  };
}

interface ServicesApiNewsletterRecipientsUpdate {
  newsletter: {
    update_type: 'recipients';
    filters?: string;
    send_percentage: number;
    send_to_unsubscribed: boolean;
    send_to_everyone?: boolean;
    deduped?: boolean;
    subscription_topic_id?: string;
  };
}

interface ServicesApiNewsletterSendUpdate {
  newsletter: {
    update_type: 'send';
    sto_enabled?: boolean;
    rate_limit_email_rate?: number;
    rate_limit_time_period?: number;
  };
}

interface ServicesApiNewsletterMainUpdate {
  newsletter: {
    update_type: '' | 'main';
    name: string;
  };
}

type ServicesApiUpdateNewsletterBody =
  | ServicesApiNewsletterChannelUpdate
  | ServicesApiNewsletterRecipientsUpdate
  | ServicesApiNewsletterSendUpdate
  | ServicesApiNewsletterMainUpdate;

interface ServicesApiUpdateNewsletterResponse {
  newsletter: {
    id: number;
    name: string;
    channel: string;
    template_id: string;
    template_ids: string[];
    send_percentage: number;
    sent_at?: number;
  };
}

interface ServicesApiGetTemplateByIdPathParams {
  environment_id: number;
  template_id: number;
}

interface ServicesApiTemplate {
  id: number;
  name: string;
  subject: string;
  body: string;
  template_type: string;
  language: string;
  editor: string | null;
  preprocessor: string;
  layout_id: number | null;
}

interface ServicesApiPreviewEmailBody {
  body: string;
  subject: string;
  layout: string;
  preprocessor: string;
  headers: unknown[];
  variables: Record<string, unknown>;
}

interface ServicesApiPreviewEmailResponse {
  body: string;
  subject: string;
  errors: Record<string, string[]>;
}

interface ServicesApiUpdateTemplatePathParams {
  environment_id: number;
  template_id: number;
}

interface ServicesApiUpdateTemplateBody {
  template: {
    layout_id?: number;
    from_identity_id?: number;
    reply_to_identity_id?: number;
    subject?: string;
    name?: string;
    body?: string;
    preheader_text?: string;
  };
}

interface ServicesApiUpdateTemplateResponse {
  template: {
    id: number;
    name: string;
    subject: string;
    layout_id: number;
    from_identity_id: number;
  };
}

/**
 * Deliverability API Types
 */

export interface ServicesApiInboxProviderMetrics {
  attempted: number[];
  bounced: number[];
  clicked: number[];
  converted: number[];
  created: number[];
  deferred: number[];
  delivered: number[];
  drafted: number[];
  failed: number[];
  human_clicked: number[];
  human_opened: number[];
  opened: number[];
  prefetch_opened: number[];
  sent: number[];
  spammed: number[];
  suppressed: number[];
  topic_unsubscribed: number[];
  undeliverable: number[];
  unsubscribed: number[];
}

export interface ServicesApiGetDeliverabilityMetricsResponse {
  inbox_provider_metrics: {
    [provider: string]: ServicesApiInboxProviderMetrics;
  };
}

interface ServicesApiGetDeliverabilityMetricsPathParams {
  environment_id: number;
}

interface ServicesApiGetDeliverabilityMetricsQueryParams {
  version: '2';
  start: number; // Unix timestamp
  end: number; // Unix timestamp
  res: 'days';
}

/**
 * Dev Tools - Populate Workspace Types
 */
interface PopulateWorkspaceBody {
  customers?: number;
  prefix?: string;
  attributes?: string[];
  events_per_person?: number;
  event_names?: string[];
  event_days_back?: number;
  include_event_attrs?: boolean;
  page_views_per_person?: number;
  anon_visitors?: number;
  anon_page_views_per_visitor?: number;
}

interface PopulateWorkspaceResponse {
  people_created: number;
  events_created: number;
  page_views_created: number;
  anon_page_views_created: number;
}

/**
 * Dashboard Metrics API Types
 *
 * These types model the responses from the metrics v2 endpoints used by the
 * dashboard page.  All v2 endpoints share a common query-parameter shape
 * (version=2, res, start, end, tz).
 */

/** Channel type names returned by the backend (see libraries/channeltype). */
export type ServicesApiChannelType =
  | 'email'
  | 'webhook'
  | 'twilio'
  | 'urban_airship'
  | 'slack'
  | 'push'
  | 'in_app'
  | 'line'
  | 'inbox'
  | 'whatsapp';

/**
 * The set of metric names present in every per-channel delivery metric object.
 *
 * Extends ServicesApiInboxProviderMetrics (which covers deliverability) with
 * two additional fields the backend includes in general delivery metrics:
 * `machine_clicked` and `replied`.
 */
export interface ServicesApiMetricValues extends ServicesApiInboxProviderMetrics {
  machine_clicked: number[];
  replied: number[];
}

// ---------------------------------------------------------------------------
// All Deliveries  –  GET /environments/:id/metrics/all_deliveries
// ---------------------------------------------------------------------------
export interface ServicesApiAllDeliveriesMetric {
  type: ServicesApiChannelType;
  values: ServicesApiMetricValues;
}

interface ServicesApiGetAllDeliveriesResponse {
  metrics: ServicesApiAllDeliveriesMetric[];
}

interface ServicesApiGetAllDeliveriesPathParams {
  environment_id: number;
}

interface ServicesApiGetAllDeliveriesQueryParams {
  version: '2';
  res: 'days' | 'months';
  start: number; // Unix timestamp
  end: number; // Unix timestamp
  tz: string; // IANA timezone, e.g. "Etc/GMT+4"
}

// ---------------------------------------------------------------------------
// All Links  –  GET /environments/:id/metrics/all_links
// ---------------------------------------------------------------------------
interface ServicesApiLinkStats {
  link_id: string;
  href: string;
  clicked: number[];
  human_clicked: number[];
}

interface ServicesApiTrackedLinkMetrics {
  environment_id: string;
  campaign_id: string | null;
  template_id: string | null;
  action_id?: string | null;
  period: string;
  delivery_type: string;
  /** Null when no tracked links exist (Go nil slice → JSON null). */
  values: ServicesApiLinkStats[] | null;
}

interface ServicesApiGetAllLinksResponse {
  tracked_link_metric: ServicesApiTrackedLinkMetrics;
}

interface ServicesApiGetAllLinksPathParams {
  environment_id: number;
}

interface ServicesApiGetAllLinksQueryParams {
  version: '2';
  res: 'days' | 'months';
  start: number; // Unix timestamp
  end: number; // Unix timestamp
  tz: string;
}

// ---------------------------------------------------------------------------
// Dashboard Tests  –  GET /environments/:id/metrics/dashboard_tests
// ---------------------------------------------------------------------------
/**
 * Go serializes these ID fields with the `json:",string"` tag (*int → JSON string),
 * unlike ServicesApiSplitAction which uses plain int → JSON number.
 */
interface ServicesApiSplitMetric {
  id: string;
  action_id: string;
  campaign_id: string;
  template_id: string;
  period: string;
  delivered: number[];
  sent: number[];
  opened: number[];
  human_opened: number[];
  clicked: number[];
  human_clicked: number[];
  converted: number[];
}

interface ServicesApiSplitAction {
  id: number;
  name: string;
  campaign_id: number;
  campaign_name: string;
  campaign_type: string;
  action_state: string;
  split_state: string;
  started: number;
  action_ids: number[];
  split_metrics: ServicesApiSplitMetric[];
}

/** Go serializes `id` with `json:"id,string"` tag (int → JSON string). */
interface ServicesApiDashboardNewsletter {
  id: string;
  name: string;
  sent_at: number;
  variation_count: number;
  automatic_winner: boolean;
  send_percentage: number;
  sent_to_remaining_at: number;
  test_duration: number;
  test_metric: string;
}

interface ServicesApiGetDashboardTestsResponse {
  split_actions: ServicesApiSplitAction[] | null;
  newsletters: ServicesApiDashboardNewsletter[] | null;
}

interface ServicesApiGetDashboardTestsPathParams {
  environment_id: number;
}

interface ServicesApiGetDashboardTestsQueryParams {
  version: '2';
  res: 'days' | 'months';
  start: number; // Unix timestamp
  end: number; // Unix timestamp
  tz: string;
}

// ---------------------------------------------------------------------------
// Segment Membership  –  GET /environments/:id/metrics/segment_membership
// Note: this endpoint only supports v1 (no version param, uses resolution/start/end).
// ---------------------------------------------------------------------------
interface ServicesApiSegmentMembership {
  resolution: string;
  segment_id: number;
  start: number; // Unix timestamp
  end: number; // Unix timestamp
  total: number[];
  entered: number[];
  left: number[];
}

interface ServicesApiGetSegmentMembershipResponse {
  membership_metric: ServicesApiSegmentMembership;
}

interface ServicesApiGetSegmentMembershipPathParams {
  environment_id: number;
}

interface ServicesApiGetSegmentMembershipQueryParams {
  segment_id: number;
  resolution: 'days' | 'weeks' | 'months';
  start: number; // Unix timestamp
  end: number; // Unix timestamp
}

// ---------------------------------------------------------------------------
// Analysis  –  GET /environments/:id/metrics/analysis
// ---------------------------------------------------------------------------

/** Per-item delivery metrics keyed by channel then metric name. */
type ServicesApiDeliveryMetricsByChannel = Record<string, Record<string, number[]>>;

interface ServicesApiAnalysisData {
  delivery_metrics?: ServicesApiDeliveryMetricsByChannel;
  journey_metrics?: Record<string, number[]>;
  newsletter_metrics?: ServicesApiDeliveryMetricsByChannel;
  transactional_metrics?: ServicesApiDeliveryMetricsByChannel;
  message_metrics?: Record<string, number[]>;
  subscription_topic_metrics?: Record<string, Record<string, number[]>>;
}

interface ServicesApiAnalysisMetric {
  campaign_id?: number;
  newsletter_id?: number;
  transactional_id?: number;
  is_default?: boolean;
  name?: string;
  type?: string;
  data: ServicesApiAnalysisData;
  conversion: boolean;
  tracked_links: boolean;
}

interface ServicesApiGetAnalysisResponse {
  metrics: ServicesApiAnalysisMetric[];
}

interface ServicesApiGetAnalysisPathParams {
  environment_id: number;
}

interface ServicesApiGetAnalysisQueryParams {
  version: '2';
  res: 'days' | 'months';
  start: number; // Unix timestamp
  end: number; // Unix timestamp
  tz: string;
  name?: string;
  /** Backend reads as repeated key: data_type=campaign&data_type=newsletter */
  data_type?: string | string[];
  /** Backend reads as repeated key: tag_ids=1&tag_ids=2 */
  tag_ids?: string | string[];
  /** Backend reads as repeated key: status=active&status=paused */
  status?: string | string[];
}

// ---------------------------------------------------------------------------
// Segment Metrics  –  GET /environments/:id/segments/:segment_id/metrics
// ---------------------------------------------------------------------------
interface ServicesApiGetSegmentMetricsResponse {
  total_customer_count: number;
  customer_count: number;
  subscribed_count: number;
  messaged_this_week_people_count: number;
  opened_this_week_people_count: number;
  clicked_this_week_people_count: number;
  active_this_week_people_count: number;
  unsubscribed_this_week_people_count: number;
  state: 'complete' | 'incomplete';
}

interface ServicesApiGetSegmentMetricsPathParams {
  environment_id: number;
  segment_id: number;
}

// ---------------------------------------------------------------------------
// Workspace Settings  –  GET /environments/:id/settings/workspace
// ---------------------------------------------------------------------------

interface ServicesApiWorkspaceSettings {
  customer_columns?: string[];
  metrics_timezone?: string;
  journey_metrics_timezone?: string;
  max_collection_size?: number;
  object_type_columns?: Record<string, string[]>;
  relationship_columns?: Record<string, string[]>;
}

interface ServicesApiGetWorkspaceSettingsResponse {
  settings: ServicesApiWorkspaceSettings;
}

interface ServicesApiGetWorkspaceSettingsPathParams {
  environment_id: number;
}

// ---------------------------------------------------------------------------
// Recently Edited  –  GET /environments/:id/recents
// ---------------------------------------------------------------------------

export interface ServicesApiRecentResource {
  id: string;
  type: 'campaign' | 'newsletter' | 'transactional' | 'segment' | 'in_app_broadcast' | 'ds_node' | 'snippet';
  updated_at: number; // Unix timestamp
}

interface ServicesApiGetRecentsPathParams {
  environment_id: number;
}

interface ServicesApiGetRecentsQueryParams {
  count?: number;
}

// ---------------------------------------------------------------------------
// Recently Shipped  –  GET /environments/:id/recents/shipped
// ---------------------------------------------------------------------------

/** A recently shipped (launched) resource with actor info. */
export interface ServicesApiShippedResource {
  id: string;
  type: 'campaign' | 'newsletter' | 'transactional' | 'in_app_broadcast';
  action: string; // "created" | "updated"
  updated_at: number; // Unix timestamp
  user_id: number;
  user_name: string;
  profile_image_url?: string;
}

interface ServicesApiGetRecentlyShippedPathParams {
  environment_id: number;
}

interface ServicesApiGetRecentlyShippedQueryParams {
  count?: number;
}

/**
 * Snippets API Types
 */

export interface ServicesApiSnippet {
  id: number;
  name: string;
  value: string;
  updated_at: number;
}

export interface ServicesApiSnippetWithUsage extends ServicesApiSnippet {
  /** Sideloaded `TemplateItem` ids — strings like `"message_item_<id>"`. */
  message_item_ids: string[];
  in_use: boolean;
}

export interface ServicesApiSnippetUsageSummary {
  message_item_ids: string[];
  in_use: boolean;
}

/**
 * Sideloaded template-item carrying snippet usage info. Mirrors
 * `model.TemplateItem` in `ui_api/model/search.go` — every id is a string.
 */
export interface ServicesApiSnippetMessageItem {
  id: string;
  name: string;
  delivery_type: string;
  status: string;
  subject: string;
  created_at: number;
  updated_at: number;
  used_in: string;
  parent_status: string;
  action_id?: string;
  campaign_id?: string;
  newsletter_id?: string;
  transactional_message_id?: string;
  in_app_broadcast_id?: string;
  template_id: string;
  from_address?: string;
  reply_to?: string;
  bcc?: string;
  preheader_text?: string;
  fields_matched?: string[];
  highlight?: string;
}

interface ServicesApiGetSnippetsPathParams {
  environment_id: number;
}

interface ServicesApiGetSnippetsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  sort?: string;
  asc?: boolean;
}

export interface ServicesApiGetSnippetsResponse {
  snippets: ServicesApiSnippet[];
  meta: { pagination: { page: number; size: number; total: number } };
}

interface ServicesApiGetSnippetByIdPathParams {
  environment_id: number;
  id: number;
}

export interface ServicesApiGetSnippetByIdResponse {
  snippet: ServicesApiSnippetWithUsage;
  message_items: ServicesApiSnippetMessageItem[];
  usage_loaded: boolean;
}

interface ServicesApiGetSnippetUsagePathParams {
  environment_id: number;
  id: number;
  continuation: number;
}

export interface ServicesApiGetSnippetUsageResponse {
  snippet: ServicesApiSnippetUsageSummary;
  message_items: ServicesApiSnippetMessageItem[] | null;
  continuation: number;
  more: boolean;
}

interface ServicesApiCreateSnippetPathParams {
  environment_id: number;
}

interface ServicesApiCreateSnippetBody {
  snippet: {
    name: string;
    value: string;
  };
}

interface ServicesApiCreateSnippetResponse {
  snippet: ServicesApiSnippet;
}

interface ServicesApiUpdateSnippetPathParams {
  environment_id: number;
  id: number;
}

interface ServicesApiUpdateSnippetBody {
  snippet: {
    name?: string;
    value?: string;
  };
}

interface ServicesApiUpdateSnippetResponse {
  snippet: ServicesApiSnippet;
}

interface ServicesApiDeleteSnippetPathParams {
  environment_id: number;
  id: number;
}

interface ServicesApiGetObjectTypeSnippetsPathParams {
  environment_id: number;
}

interface ServicesApiGetObjectTypeSnippetsQueryParams {
  'objectTypeIds[]': string[];
}

interface ServicesApiGetObjectTypeSnippetsResponse {
  snippets: ServicesApiSnippet[];
}

/**
 * Service Account Types
 */

export interface ServicesApiServiceAccount {
  id: number;
  account_id: number;
  name: string;
  description: string;
  account_type: 'user' | 'agent' | 'system';
  created_by: number;
  created_at: number;
  updated_at: number;
}

export interface ServicesApiServiceAccountToken {
  id: number;
  name: string;
  hint: string;
  created_by: number;
  read_only: boolean;
  expires_at?: number;
  last_used_at?: number;
  created_at: number;
  revoked_at?: number;
}

interface ServicesApiCreateTokenResponse {
  token: string;
  id: number;
  name: string;
  hint: string;
  read_only: boolean;
  expires_at?: number;
  created_at: number;
}

interface ServicesApiSigningSecretResponse {
  signing_secret: {
    secret: string;
    environment_id: number;
  };
}

interface ServicesApiServiceAccountPathParams {
  accountId: number;
  serviceAccountId: number;
}

interface ServicesApiTokenPathParams extends ServicesApiServiceAccountPathParams {
  tokenId: number;
}

/**
 * Assets API Types
 */

export interface ServicesApiAssetUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
}

export interface ServicesApiAsset {
  id: string;
  parent_id: string | null;
  is_dir: boolean;
  in_use: boolean;
  name: string;
  path: string;
  size: number;
  created_at: number;
  created_by: ServicesApiAssetUser;
  updated_at: number;
  updated_by: ServicesApiAssetUser;
}

export interface ServicesApiAssetPaginationMeta {
  pagination: {
    page: number;
    size: number;
    total: number;
  };
}

interface ServicesApiListAssetsPathParams {
  environment_id: number;
}

interface ServicesApiListAssetsQueryParams {
  folder_id?: string;
  order?: string;
  page?: string;
  search?: string;
  sort?: string;
  limit?: string | number;
}

export interface ServicesApiListAssetsResponse {
  assets: ServicesApiAsset[];
  parents: ServicesApiAsset[];
  meta: ServicesApiAssetPaginationMeta;
}

interface ServicesApiCreateAssetPathParams {
  environment_id: number;
}

interface ServicesApiCreateAssetBody {
  asset: {
    name: string;
    is_dir: boolean;
    parent_id?: string | null;
    path?: string;
    size?: number;
  };
  file_type?: string;
}

interface ServicesApiCreateAssetResponse {
  asset: ServicesApiAsset;
}

interface ServicesApiMoveAssetsPathParams {
  environment_id: number;
}

interface ServicesApiMoveAssetsBody {
  assets: Array<{
    asset: {
      id: string;
      name: string;
      is_dir: boolean;
      parent_id: string | null;
      path: string;
      size: number;
    };
  }>;
}

interface ServicesApiDeleteAssetsPathParams {
  environment_id: number;
}

interface ServicesApiDeleteAssetsBody {
  asset_ids: number[];
}

/**
 * Geofence API Types
 */

interface ServicesApiGeofence {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  radius: number; // meters
  tag: string;
  external_id: string;
  metadata: Record<string, unknown>;
  geofence_set_id: number;
  created_at: number; // unix timestamp
  updated_at: number;
}

interface ServicesApiListGeofencesPathParams {
  environment_id: number;
}

interface ServicesApiListGeofencesQuery {
  page?: number;
  page_size?: number;
}

interface ServicesApiListGeofencesResponse {
  geofences: ServicesApiGeofence[];
  meta: { page: number; page_size: number; total: number };
}

interface ServicesApiGeofencePathParams {
  environment_id: number;
  id: number;
}

interface ServicesApiGetGeofenceResponse {
  geofence: ServicesApiGeofence;
}

interface ServicesApiCreateGeofenceBody {
  name: string;
  latitude: number;
  longitude: number;
  radius: number; // meters
}

type ServicesApiUpdateGeofenceBody = Partial<ServicesApiCreateGeofenceBody>;

const c = initContract();
const contract = c.router({
  /**
   * Account API
   */
  getAccount: {
    method: 'GET',
    path: '/accounts/current',
    responses: {
      200: c.type<ServicesApiAccountResponse>(),
    },
    summary: 'Get account data based on account ID',
  },
  getAccountById: {
    method: 'GET',
    path: '/accounts/:accountId',
    pathParams: c.type<ServicesApiGetAccountByIdPathParams>(),
    responses: {
      200: c.type<ServicesApiAccountResponse>(),
    },
    summary: 'Get account data based on account ID',
  },
  getAccountByEnvironmentId: {
    method: 'GET',
    path: '/account_by_environment/:environmentId',
    pathParams: c.type<ServicesApiGetAccountByEnvironmentIdPathParams>(),
    responses: {
      200: c.type<ServicesApiAccountResponse>(),
    },
    summary: 'Get account data based on environment ID',
  },
  /**
   * Environment API
   */
  getEnvironment: {
    method: 'GET',
    path: '/environments/:environmentId',
    pathParams: c.type<ServicesApiGetEnvironmentPathParams>(),
    responses: {
      200: c.type<ServicesApiGetEnvironmentResponse>(),
    },
    summary: 'Get environment details by ID',
  },
  /**
   * Account Verification API
   */
  setSupportAccess: {
    method: 'PUT',
    path: '/accounts/:accountId/set_support_access',
    pathParams: c.type<ServicesApiSetSupportAccessPathParams>(),
    body: c.type<ServicesApiSetSupportAccessBody>(),
    responses: {
      200: c.type<void>(),
    },
    summary: 'Set support team access consent',
  },
  submitSupportRequest: {
    method: 'POST',
    path: '/accounts/:accountId/support',
    pathParams: c.type<ServicesApiSubmitSupportRequestPathParams>(),
    body: c.type<ServicesApiSubmitSupportRequestBody>(),
    responses: {
      200: c.type<void>(),
    },
    summary: 'Submit a support request',
  },
  submitPremiumRequest: {
    method: 'POST',
    path: '/accounts/:accountId/premium',
    pathParams: c.type<{ accountId: number }>(),
    body: c.type<ServicesApiSubmitPremiumContactBody>(),
    responses: {
      204: c.type<void>(),
    },
    summary: 'Submit a premium plan inquiry',
  },
  getBuilderCredits: {
    method: 'GET',
    path: '/accounts/:accountId/builder_credits',
    pathParams: c.type<{ accountId: number }>(),
    responses: {
      200: c.type<ServicesApiGetBuilderCreditsResponse>(),
    },
    summary: 'Get builder credit grants for an account',
  },
  verifyAccount: {
    method: 'POST',
    path: '/accounts/:accountId/verify2',
    pathParams: c.type<{ accountId: string }>(),
    body: c.type<{
      email: string;
      website: string;
      privacy_policy_url?: string;
      terms_of_service_url?: string;
    }>(),
    responses: {
      200: c.type<{ job_id: string }>(),
    },
    summary: 'Verify account for email sending',
  },
  verifyAccountStatus: {
    method: 'GET',
    path: '/accounts/:accountId/verify2/:jobId',
    pathParams: c.type<{ accountId: string; jobId: string }>(),
    responses: {
      200: c.type<ServicesApiVerifyAccountStatusResponse>(),
    },
    summary: 'Get account verification status',
  },
  verifyAccountCurrentJob: {
    method: 'GET',
    path: '/accounts/:accountId/verify2/current',
    pathParams: c.type<{ accountId: string }>(),
    responses: {
      200: c.type<{
        job_id: string | null;
      }>(),
    },
    summary: 'Get current active account verification job ID',
  },

  /**
   * Session Metadata API
   */
  getSessionMetadata: {
    method: 'GET',
    path: '/sessions/metadata',
    responses: {
      200: c.type<ServicesApiGetSessionMetadataResponse>(),
    },
    summary: 'Get metadata for all active session cookies',
  },
  logoutSession: {
    method: 'POST',
    path: '/sessions/logout/:id',
    pathParams: c.type<{ id: number }>(),
    body: c.type<Record<string, never>>(),
    responses: {
      204: c.type<void>(),
    },
    summary: 'Revoke the session at a single cookie slot index',
  },

  /**
   * CLI Login API
   */
  loginCli: {
    method: 'POST',
    path: '/login_cli',
    body: c.type<ServicesApiLoginCliBody>(),
    responses: {
      200: c.type<ServicesApiLoginCliResponse>(),
    },
    summary: 'Generate a one-time CLI service account token',
  },

  /**
   * CLI → Web handoff: exchange the short-lived JWT in the URL for a
   * browser session. The CLI prints a URL containing this JWT after the
   * user runs `cio auth login`.
   */
  loginCliLinkExchange: {
    method: 'POST',
    path: '/login_cli/link/exchange',
    body: c.type<ServicesApiLoginCliLinkExchangeBody>(),
    responses: {
      200: c.type<ServicesApiLoginCliLinkExchangeResponse>(),
    },
    summary: 'Exchange a CLI handoff JWT for a browser session',
  },

  /**
   * Copilot API
   */
  createCopilotSession: {
    method: 'POST',
    path: '/accounts/:accountId/copilot',
    pathParams: c.type<ServicesApiCreateCopilotSessionPathParams>(),
    body: c.type<object>(),
    responses: {
      200: c.type<ServicesApiCreateCopilotSessionResponse>(),
    },
    summary: 'Start a new copilot session',
  },
  deleteCopilotSession: {
    method: 'DELETE',
    path: '/accounts/:accountId/copilot/:sessionId',
    pathParams: c.type<ServicesApiDeleteCopilotSessionPathParams>(),
    responses: {
      200: c.type<void>(),
    },
    summary: 'Stop a copilot session',
  },
  getCopilotSession: {
    method: 'GET',
    path: '/accounts/:accountId/copilot/:sessionId',
    pathParams: c.type<ServicesApiGetCopilotSessionPathParams>(),
    responses: {
      200: c.type<ServicesApiGetCopilotSessionResponse>(),
    },
    summary: 'Get messages from a copilot session',
  },
  getCopilotFileUploadSignature: {
    method: 'POST',
    path: '/accounts/:accountId/copilot/:sessionId/sign',
    pathParams: c.type<ServicesApiGetCopilotFileUploadSignaturePathParams>(),
    body: c.type<ServicesApiGetCopilotFileUploadSignatureBody>(),
    responses: {
      200: c.type<ServicesApiGetCopilotFileUploadSignatureResponse>(),
    },
    summary: 'Get signed URL for file upload to GCS',
  },
  getCopilotFileDownloadUrl: {
    method: 'GET',
    path: '/accounts/:accountId/copilot/:sessionId/files/:filename',
    pathParams: c.type<ServicesApiGetCopilotFileDownloadUrlPathParams>(),
    responses: {
      200: c.type<ServicesApiGetCopilotFileDownloadUrlResponse>(),
    },
    summary: 'Get signed URL for file download from GCS',
  },
  updateCopilotSession: {
    method: 'PUT',
    path: '/accounts/:accountId/copilot/:sessionId',
    pathParams: c.type<ServicesApiUpdateCopilotSessionPathParams>(),
    body: c.type<ServicesApiUpdateCopilotSessionBody>(),
    responses: {
      200: c.type<void>(),
    },
    summary: 'Update copilot session name',
  },
  getCopilotSessions: {
    method: 'GET',
    path: '/accounts/:accountId/copilot',
    pathParams: c.type<ServicesApiGetCopilotSessionsPathParams>(),
    query: c.type<ServicesApiGetCopilotSessionsQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetCopilotSessionsResponse>(),
    },
    summary: 'Get all copilot sessions',
  },
  getCopilotVirtualFile: {
    method: 'GET',
    path: '/accounts/:accountId/copilot_files/:filePath',
    pathParams: c.type<ServicesApiGetCopilotVirtualFilePathParams>(),
    responses: {
      200: c.type<ServicesApiGetCopilotVirtualFileResponse>(),
    },
    summary: 'Get a copilot virtual file by path',
  },
  listCopilotFiles: {
    method: 'GET',
    path: '/accounts/:accountId/copilot_files',
    pathParams: c.type<ServicesApiListCopilotFilesPathParams>(),
    responses: {
      200: c.type<ServicesApiListCopilotFilesResponse>(),
    },
    summary: 'List all copilot files for the authenticated user',
  },
  updateCopilotFile: {
    method: 'PUT',
    path: '/accounts/:accountId/copilot_files/:filePath',
    pathParams: c.type<ServicesApiUpdateCopilotFilePathParams>(),
    body: c.type<ServicesApiUpdateCopilotFileBody>(),
    responses: {
      200: c.type<ServicesApiUpdateCopilotFileResponse>(),
    },
    summary: 'Create or update a copilot file',
  },
  deleteCopilotFile: {
    method: 'DELETE',
    path: '/accounts/:accountId/copilot_files/:filePath',
    pathParams: c.type<ServicesApiDeleteCopilotFilePathParams>(),
    body: c.type<Record<string, never>>(),
    responses: {
      200: c.type<void>(),
    },
    summary: 'Delete a copilot file',
  },
  recordCopilotFeedback: {
    method: 'POST',
    path: '/accounts/:accountId/copilot/:sessionId/logs/:logId/feedback',
    pathParams: c.type<ServicesApiRecordCopilotFeedbackPathParams>(),
    body: c.type<ServicesApiRecordCopilotFeedbackBody>(),
    responses: {
      200: c.type<ServicesApiRecordCopilotFeedbackResponse>(),
    },
    summary: 'Record feedback for a copilot response',
  },
  getAIContextOverrides: {
    method: 'GET',
    path: '/environments/:workspaceId/ai_context/overrides',
    pathParams: c.type<ServicesApiGetAIContextOverridesPathParams>(),
    responses: {
      200: c.type<ServicesApiGetAIContextOverridesResponse>(),
    },
    summary: 'Get AI context overrides',
  },
  updateAIContextOverrides: {
    method: 'PUT',
    path: '/environments/:workspaceId/ai_context/overrides',
    pathParams: c.type<ServicesApiUpdateAIContextOverridesPathParams>(),
    body: c.type<ServicesApiUpdateAIContextOverridesBody>(),
    responses: {
      200: c.type<ServicesApiUpdateAIContextOverridesResponse>(),
    },
    summary: 'Update AI context overrides',
  },

  /**
   * Copilot Cron (Routines) API
   */
  listCopilotCrons: {
    method: 'GET',
    path: '/environments/:environment_id/copilot_crons',
    pathParams: c.type<ServicesApiListCopilotCronsPathParams>(),
    responses: {
      200: c.type<ServicesApiListCopilotCronsResponse>(),
    },
    summary: 'List copilot cron jobs (routines) for the current user in a workspace',
  },
  getCopilotCron: {
    method: 'GET',
    path: '/environments/:environment_id/copilot_crons/:cron_id',
    pathParams: c.type<ServicesApiGetCopilotCronPathParams>(),
    responses: {
      200: c.type<ServicesApiGetCopilotCronResponse>(),
    },
    summary: 'Get a single copilot cron job by ID',
  },
  updateCopilotCron: {
    method: 'PUT',
    path: '/environments/:environment_id/copilot_crons/:cron_id',
    pathParams: c.type<ServicesApiUpdateCopilotCronPathParams>(),
    body: c.type<ServicesApiUpdateCopilotCronBody>(),
    responses: {
      200: c.type<ServicesApiUpdateCopilotCronResponse>(),
    },
    summary: 'Update a copilot cron job (name, prompt, schedule, or paused state)',
  },
  deleteCopilotCron: {
    method: 'DELETE',
    path: '/environments/:environment_id/copilot_crons/:cron_id',
    pathParams: c.type<ServicesApiDeleteCopilotCronPathParams>(),
    body: c.type<Record<string, never>>(),
    responses: {
      200: c.type<void>(),
    },
    summary: 'Delete a copilot cron job',
  },

  /**
   * Workramp API
   */
  getWorkrampAuthUrl: {
    method: 'GET',
    path: '/accounts/:accountId/users/:userId/workramp/auth_url',
    pathParams: c.type<ServicesApiGetWorkrampAuthUrlPathParams>(),
    query: c.type<ServicesApiGetWorkrampAuthUrlQuery>(),
    responses: {
      200: c.type<ServicesApiGetWorkrampAuthUrlResponse>(),
    },
    summary: 'Get Workramp authenticated URL',
  },

  /**
   * User Feedback API
   */
  submitFeedback: {
    method: 'POST',
    path: '/accounts/:accountId/feedback',
    pathParams: c.type<ServicesApiSubmitFeedbackPathParams>(),
    body: c.type<ServicesApiSubmitFeedbackBody>(),
    responses: {
      200: c.type<void>(),
    },
    summary: 'Submit user feedback',
  },

  /**
   * OAuth2 MCP API
   */
  oauthMcp: {
    method: 'POST',
    path: '/oauth2/mcp',
    contentType: 'application/x-www-form-urlencoded',
    body: c.type<string>(),
    responses: {
      200: c.type<ServicesApiOAuthMcpResponse>(),
    },
    summary: 'Handle OAuth2 authorization for MCP',
  },

  /**
   * Zendesk Chat API
   */
  getZendeskChatAccessToken: {
    method: 'GET',
    path: '/accounts/:accountId/users/:userId/zendesk_chat',
    pathParams: c.type<ServicesApiGetZendeskChatAccessTokenPathParams>(),
    responses: {
      200: c.type<ServicesApiGetZendeskChatAccessTokenResponse>(),
    },
    summary: 'Get Zendesk chat access token and configuration',
  },
  refreshZendeskChatToken: {
    method: 'POST',
    path: '/accounts/:accountId/users/:userId/zendesk_chat/token',
    pathParams: c.type<ServicesApiRefreshZendeskChatTokenPathParams>(),
    body: c.type<void>(),
    responses: {
      200: c.type<ServicesApiRefreshZendeskChatTokenResponse>(),
    },
    summary: 'Generate new Zendesk chat token',
  },

  /**
   * Tracking API
   */
  trackEvent: {
    method: 'POST',
    path: '/environments/:workspaceId/track_event',
    pathParams: c.type<ServicesApiTrackEventPathParams>(),
    body: c.type<ServicesApiTrackEventBody>(),
    responses: {
      200: c.type<void>(),
    },
    summary: 'Track an event',
  },

  /**
   * Audit Logs API
   */
  getAllUsers: {
    method: 'GET',
    path: '/accounts/:account_id/all_users',
    pathParams: c.type<ServicesApiGetAllUsersPathParams>(),
    responses: {
      200: c.type<ServicesApiGetAllUsersResponse>(),
    },
    summary: 'Get all users for an account',
  },
  getAccountAuditLogs: {
    method: 'GET',
    path: '/accounts/:account_id/audit_logs',
    pathParams: c.type<ServicesApiGetAccountAuditLogsPathParams>(),
    query: c.type<ServicesApiAuditLogsQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetAuditLogsResponse>(),
    },
    summary: 'Get audit logs for an account',
  },
  getWorkspaceAuditLogs: {
    method: 'GET',
    path: '/environments/:environment_id/audit_logs',
    pathParams: c.type<ServicesApiGetWorkspaceAuditLogsPathParams>(),
    query: c.type<ServicesApiAuditLogsQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetAuditLogsResponse>(),
    },
    summary: 'Get audit logs for a workspace',
  },
  getAccountAuditLogById: {
    method: 'GET',
    path: '/accounts/:account_id/audit_log/:id',
    pathParams: c.type<ServicesApiGetAccountAuditLogByIdPathParams>(),
    query: c.type<ServicesApiGetAuditLogByIdQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetAuditLogByIdResponse>(),
    },
    summary: 'Get a single audit log by ID for an account',
  },
  getWorkspaceAuditLogById: {
    method: 'GET',
    path: '/environments/:environment_id/audit_log/:id',
    pathParams: c.type<ServicesApiGetWorkspaceAuditLogByIdPathParams>(),
    query: c.type<ServicesApiGetAuditLogByIdQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetAuditLogByIdResponse>(),
    },
    summary: 'Get a single audit log by ID for a workspace',
  },
  exportAccountAuditLogs: {
    method: 'POST',
    path: '/accounts/:account_id/audit_logs/export',
    pathParams: c.type<ServicesApiExportAccountAuditLogsPathParams>(),
    body: c.type<ServicesApiExportAuditLogsBody>(),
    responses: {
      200: c.type<ServicesApiExportAuditLogsResponse>(),
    },
    summary: 'Create an export of account-level audit logs',
  },
  exportWorkspaceAuditLogs: {
    method: 'POST',
    path: '/environments/:environment_id/audit_logs/export',
    pathParams: c.type<ServicesApiExportWorkspaceAuditLogsPathParams>(),
    body: c.type<ServicesApiExportAuditLogsBody>(),
    responses: {
      200: c.type<ServicesApiExportAuditLogsResponse>(),
    },
    summary: 'Create an export of workspace-level audit logs',
  },

  /**
   * Search API
   */
  search: {
    method: 'GET',
    path: '/environments/:workspaceId/search',
    pathParams: c.type<ServicesApiSearchPathParams>(),
    query: c.type<ServicesApiSearchQueryParams>(),
    responses: {
      200: c.type<ServicesApiSearchResponse>(),
    },
    summary: 'Search across workspace resources',
  },

  /**
   * Geofences API
   */
  listGeofences: {
    method: 'GET',
    path: '/environments/:environment_id/geofences',
    pathParams: c.type<ServicesApiListGeofencesPathParams>(),
    query: c.type<ServicesApiListGeofencesQuery>(),
    responses: {
      200: c.type<ServicesApiListGeofencesResponse>(),
    },
    summary: 'List geo-fences for an environment',
  },
  getGeofence: {
    method: 'GET',
    path: '/environments/:environment_id/geofences/:id',
    pathParams: c.type<ServicesApiGeofencePathParams>(),
    responses: {
      200: c.type<ServicesApiGetGeofenceResponse>(),
    },
    summary: 'Get a single geo-fence by ID',
  },
  createGeofence: {
    method: 'POST',
    path: '/environments/:environment_id/geofences',
    pathParams: c.type<ServicesApiListGeofencesPathParams>(),
    body: c.type<ServicesApiCreateGeofenceBody>(),
    responses: {
      200: c.type<ServicesApiGetGeofenceResponse>(),
      422: c.type<ServicesApiErrorResponse>(),
    },
    summary: 'Create a geo-fence',
  },
  updateGeofence: {
    method: 'PUT',
    path: '/environments/:environment_id/geofences/:id',
    pathParams: c.type<ServicesApiGeofencePathParams>(),
    body: c.type<ServicesApiUpdateGeofenceBody>(),
    responses: {
      200: c.type<ServicesApiGetGeofenceResponse>(),
      422: c.type<ServicesApiErrorResponse>(),
    },
    summary: 'Update a geo-fence',
  },
  deleteGeofence: {
    method: 'DELETE',
    path: '/environments/:environment_id/geofences/:id',
    pathParams: c.type<ServicesApiGeofencePathParams>(),
    responses: {
      200: c.type<void>(),
    },
    summary: 'Delete a geo-fence',
  },

  /**
   * Campaigns API
   */
  getCampaigns: {
    method: 'GET',
    path: '/environments/:environment_id/campaigns',
    pathParams: c.type<ServicesApiGetCampaignsPathParams>(),
    query: c.type<ServicesApiGetCampaignsQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetCampaignsResponse>(),
    },
    summary: 'Get all campaigns for a workspace',
  },

  getCampaignById: {
    method: 'GET',
    path: '/environments/:environment_id/campaigns/:campaign_id',
    pathParams: c.type<{ environment_id: number; campaign_id: string }>(),
    responses: {
      200: c.type<ServicesApiCampaignDetailResponse>(),
    },
    summary: 'Get a single campaign by ID with full details',
  },

  getCampaignActionStatus: {
    method: 'GET',
    path: '/environments/:environment_id/campaigns/:campaign_id/action_status',
    pathParams: c.type<{ environment_id: number; campaign_id: string }>(),
    responses: {
      200: c.type<{
        actions: Array<{
          id: string;
          subject_count: number;
          eval_stage: string;
          eval_progress: number;
          subject_count_limited: boolean;
        }>;
      }>(),
    },
    summary: 'Get runtime status (subject counts) for all actions in a campaign',
  },

  createCampaign: {
    method: 'POST',
    path: '/environments/:environment_id/campaigns',
    pathParams: c.type<{ environment_id: number }>(),
    body: c.type<ServicesApiCampaignCreateBody>(),
    responses: {
      200: c.type<ServicesApiCampaignModel>(),
    },
    summary: 'Create a campaign',
  },

  /**
   * Update a campaign. The backend dispatches on `body.campaign.update_type`
   * to one of ~11 sub-behaviors (see ui_api/campaigns/router.go for the full
   * list). Frontend convention: each update_type is its own exported action
   * hook in the campaign collection — they all hit this endpoint with
   * different body shapes.
   */
  updateCampaign: {
    method: 'PUT',
    path: '/environments/:environment_id/campaigns/:campaign_id',
    pathParams: c.type<{ environment_id: number; campaign_id: string }>(),
    body: c.type<ServicesApiCampaignUpdateBody>(),
    responses: {
      200: c.type<ServicesApiCampaignModel>(),
    },
    summary: 'Update a campaign (dispatches on body.campaign.update_type)',
  },

  deleteCampaign: {
    method: 'DELETE',
    path: '/environments/:environment_id/campaigns/:campaign_id',
    pathParams: c.type<{ environment_id: number; campaign_id: string }>(),
    body: c.type<void>(),
    responses: {
      200: c.type<Record<string, never>>(),
    },
    summary: 'Delete a campaign (returns {} immediately; deletion is async)',
  },

  /**
   * Action API
   *
   * Note: there is no standalone `DELETE /actions/:id` endpoint. Action
   * deletion goes through `updateCampaign` with `update_type: 'delete_actions'`
   * and `action_ids[]` in the body. Likewise, action *creation* goes through
   * `updateCampaign` with `update_type: 'add_actions'`. There IS a single-
   * record `GET /actions/:id` (`getActionById` below) but no standalone
   * `GET /actions` list — list-shape rows arrive via `getCampaigns` sideload.
   */
  getActionById: {
    method: 'GET',
    path: '/environments/:environment_id/actions/:action_id',
    pathParams: c.type<{ environment_id: number; action_id: string }>(),
    responses: {
      200: c.type<ServicesApiActionDetailResponse>(),
    },
    summary: 'Get a single action by ID with full details',
  },

  updateAction: {
    method: 'PUT',
    path: '/environments/:environment_id/actions/:action_id',
    pathParams: c.type<{ environment_id: number; action_id: string }>(),
    body: c.type<ServicesApiActionUpdateBody>(),
    responses: {
      200: c.type<ServicesApiActionUpdateResponse>(),
    },
    summary: 'Update an action',
  },

  createSplitTest: {
    method: 'POST',
    path: '/environments/:environment_id/actions/:action_id/test',
    pathParams: c.type<{ environment_id: number; action_id: string }>(),
    body: c.type<ServicesApiCreateSplitTestBody>(),
    responses: {
      200: c.type<ServicesApiActionSplitResponse>(),
    },
    summary: 'Convert an action into a split test (modelApiAction equivalent)',
  },

  selectSplitWinner: {
    method: 'POST',
    path: '/environments/:environment_id/actions/:action_id/winner',
    pathParams: c.type<{ environment_id: number; action_id: string }>(),
    body: c.type<ServicesApiSelectSplitWinnerBody>(),
    responses: {
      200: c.type<ServicesApiCampaignModel>(),
    },
    summary: 'Select the winner of a split test (modelApiAction equivalent)',
  },

  fetchActionPreviewVariables: {
    method: 'GET',
    path: '/environments/:environment_id/actions/:action_id/variables',
    pathParams: c.type<{ environment_id: number; action_id: string }>(),
    responses: {
      200: c.type<ServicesApiActionVariablesResponse>(),
    },
    summary: 'Fetch preview variables for an action (modelApiAction equivalent)',
  },

  /**
   * Tags API
   */
  getTags: {
    method: 'GET',
    path: '/environments/:environment_id/tags',
    pathParams: c.type<{ environment_id: number }>(),
    responses: {
      200: c.type<ServicesApiGetTagsResponse>(),
    },
    summary: 'Get all tags for a workspace',
  },

  getTagById: {
    method: 'GET',
    path: '/environments/:environment_id/tags/:tag_id',
    pathParams: c.type<{ environment_id: number; tag_id: string }>(),
    responses: {
      200: c.type<ServicesApiTagResponse>(),
    },
    summary: 'Get a tag by id',
  },

  createTag: {
    method: 'POST',
    path: '/environments/:environment_id/tags',
    pathParams: c.type<{ environment_id: number }>(),
    body: c.type<ServicesApiTagWriteBody>(),
    responses: {
      200: c.type<ServicesApiTagResponse>(),
    },
    summary: 'Create a tag',
  },

  updateTag: {
    method: 'PUT',
    path: '/environments/:environment_id/tags/:tag_id',
    pathParams: c.type<{ environment_id: number; tag_id: string }>(),
    body: c.type<ServicesApiTagWriteBody>(),
    responses: {
      200: c.type<ServicesApiTagResponse>(),
    },
    summary: 'Update a tag',
  },

  deleteTag: {
    method: 'DELETE',
    path: '/environments/:environment_id/tags/:tag_id',
    pathParams: c.type<{ environment_id: number; tag_id: string }>(),
    body: c.type<void>(),
    responses: {
      204: c.type<void>(),
    },
    summary: 'Delete a tag',
  },

  /**
   * Segments API
   */
  getSegments: {
    method: 'GET',
    path: '/environments/:environment_id/segments',
    pathParams: c.type<ServicesApiGetSegmentsPathParams>(),
    query: c.type<ServicesApiGetSegmentsQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetSegmentsResponse>(),
    },
    summary: 'Get segments for a workspace (paginated when size is set)',
  },

  /**
   * Get a single segment by ID (for segment conditions preview)
   */
  getSegmentById: {
    method: 'GET',
    path: '/environments/:environment_id/segments/:segment_id',
    pathParams: c.type<{ environment_id: number; segment_id: number }>(),
    responses: {
      200: c.type<{
        segment: ServicesApiSegment;
        tags?: Array<{ id: string; name: string }>;
      }>(),
      404: c.type<{
        error: string;
      }>(),
    },
    summary: 'Get a single segment by ID with full details including conditions',
  },

  getSegmentCount: {
    method: 'GET',
    path: '/environments/:environment_id/segments/:segment_id/count',
    pathParams: c.type<{ environment_id: number; segment_id: number }>(),
    responses: {
      200: c.type<{ count: number }>(),
    },
    summary: 'Get the number of people matching a segment',
  },

  /**
   * Newsletters API
   */
  getNewsletters: {
    method: 'GET',
    path: '/environments/:environment_id/newsletters',
    pathParams: c.type<ServicesApiGetNewslettersPathParams>(),
    query: c.type<ServicesApiGetNewslettersQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetNewslettersResponse>(),
    },
    summary: 'Get all newsletters for a workspace',
  },

  getNewsletterById: {
    method: 'GET',
    path: '/environments/:environment_id/newsletters/:id',
    pathParams: c.type<{ environment_id: number; id: number }>(),
    responses: {
      200: c.type<{ newsletter: ServicesApiNewsletter }>(),
    },
    summary: 'Get a single newsletter (broadcast) by ID',
  },

  /**
   * Transactional Messages API
   */
  getTransactionalMessages: {
    method: 'GET',
    path: '/environments/:environment_id/transactional_messages',
    pathParams: c.type<ServicesApiGetTransactionalMessagesPathParams>(),
    query: c.type<ServicesApiGetTransactionalMessagesQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetTransactionalMessagesResponse>(),
    },
    summary: 'Get all transactional messages for a workspace',
  },

  getTransactionalMessageById: {
    method: 'GET',
    path: '/environments/:environment_id/transactional_messages/:transactional_message_id',
    pathParams: c.type<{ environment_id: number; transactional_message_id: number }>(),
    responses: {
      200: c.type<{ transactional_message: ServicesApiTransactionalMessage }>(),
    },
    summary: 'Get a single transactional message by ID',
  },

  getTransactionalMessageMetrics: {
    method: 'GET',
    path: '/environments/:environment_id/transactional_messages/:transactional_message_id/metrics',
    pathParams: c.type<{ environment_id: number; transactional_message_id: number }>(),
    responses: {
      200: c.type<ServicesApiGetTransactionalMessageMetricsResponse>(),
    },
    summary: 'Get metrics for a transactional message',
  },

  /**
   * Snippets API
   */
  getSnippets: {
    method: 'GET',
    path: '/environments/:environment_id/snippets',
    pathParams: c.type<ServicesApiGetSnippetsPathParams>(),
    query: c.type<ServicesApiGetSnippetsQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetSnippetsResponse>(),
    },
    summary: 'List snippets for a workspace with pagination, search, and sorting',
  },

  getSnippetById: {
    method: 'GET',
    path: '/environments/:environment_id/snippets/:id',
    pathParams: c.type<ServicesApiGetSnippetByIdPathParams>(),
    responses: {
      200: c.type<ServicesApiGetSnippetByIdResponse>(),
    },
    summary: 'Get a single snippet by ID, including usage info',
  },

  getSnippetUsage: {
    method: 'GET',
    path: '/environments/:environment_id/snippets/:id/usage/:continuation',
    pathParams: c.type<ServicesApiGetSnippetUsagePathParams>(),
    responses: {
      200: c.type<ServicesApiGetSnippetUsageResponse>(),
    },
    summary: 'Get paginated snippet usage in messages via continuation token',
  },

  createSnippet: {
    method: 'POST',
    path: '/environments/:environment_id/snippets',
    pathParams: c.type<ServicesApiCreateSnippetPathParams>(),
    body: c.type<ServicesApiCreateSnippetBody>(),
    responses: {
      200: c.type<ServicesApiCreateSnippetResponse>(),
      422: c.type<ServicesApiErrorResponse>(),
    },
    summary: 'Create a new snippet',
  },

  updateSnippet: {
    method: 'PUT',
    path: '/environments/:environment_id/snippets/:id',
    pathParams: c.type<ServicesApiUpdateSnippetPathParams>(),
    body: c.type<ServicesApiUpdateSnippetBody>(),
    responses: {
      200: c.type<ServicesApiUpdateSnippetResponse>(),
      422: c.type<ServicesApiErrorResponse>(),
    },
    summary: 'Update an existing snippet',
  },

  deleteSnippet: {
    method: 'DELETE',
    path: '/environments/:environment_id/snippets/:id',
    pathParams: c.type<ServicesApiDeleteSnippetPathParams>(),
    body: c.type<Record<string, never>>(),
    responses: {
      200: c.type<void>(),
    },
    summary: 'Delete a snippet',
  },

  getObjectTypeSnippets: {
    method: 'GET',
    path: '/environments/:environment_id/object_type_snippets',
    pathParams: c.type<ServicesApiGetObjectTypeSnippetsPathParams>(),
    query: c.type<ServicesApiGetObjectTypeSnippetsQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetObjectTypeSnippetsResponse>(),
    },
    summary: 'Get snippets filtered by object type IDs',
  },

  getInAppBroadcastById: {
    method: 'GET',
    path: '/environments/:environment_id/in_app_broadcasts/:in_app_broadcast_id',
    pathParams: c.type<{ environment_id: number; in_app_broadcast_id: number }>(),
    responses: {
      200: c.type<{ in_app_broadcast: { id: number; name: string; state: string } }>(),
    },
    summary: 'Get a single in-app broadcast by ID',
  },

  /**
   * Backfills (Tasks) API
   */
  getBackfills: {
    method: 'GET',
    path: '/environments/:environment_id/backfills',
    pathParams: c.type<ServicesApiGetBackfillsPathParams>(),
    query: c.type<ServicesApiGetBackfillsQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetBackfillsResponse>(),
    },
    summary: 'Get backfills (background tasks) for a workspace',
  },

  /**
   * Deliverability API
   */
  getDeliverabilityMetrics: {
    method: 'GET',
    path: '/environments/:environment_id/metrics/deliverability',
    pathParams: c.type<ServicesApiGetDeliverabilityMetricsPathParams>(),
    query: c.type<ServicesApiGetDeliverabilityMetricsQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetDeliverabilityMetricsResponse>(),
    },
    summary: 'Get deliverability metrics by inbox provider',
  },

  /**
   * Dashboard Metrics API
   */
  getAllDeliveries: {
    method: 'GET',
    path: '/environments/:environment_id/metrics/all_deliveries',
    pathParams: c.type<ServicesApiGetAllDeliveriesPathParams>(),
    query: c.type<ServicesApiGetAllDeliveriesQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetAllDeliveriesResponse>(),
    },
    summary: 'Get all delivery metrics for a workspace, broken down by channel',
  },
  getAllLinks: {
    method: 'GET',
    path: '/environments/:environment_id/metrics/all_links',
    pathParams: c.type<ServicesApiGetAllLinksPathParams>(),
    query: c.type<ServicesApiGetAllLinksQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetAllLinksResponse>(),
    },
    summary: 'Get tracked link click metrics for all links in a workspace',
  },
  getDashboardTests: {
    method: 'GET',
    path: '/environments/:environment_id/metrics/dashboard_tests',
    pathParams: c.type<ServicesApiGetDashboardTestsPathParams>(),
    query: c.type<ServicesApiGetDashboardTestsQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetDashboardTestsResponse>(),
    },
    summary: 'Get active A/B tests and newsletter tests for the dashboard',
  },
  getSegmentMembership: {
    method: 'GET',
    path: '/environments/:environment_id/metrics/segment_membership',
    pathParams: c.type<ServicesApiGetSegmentMembershipPathParams>(),
    query: c.type<ServicesApiGetSegmentMembershipQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetSegmentMembershipResponse>(),
    },
    summary: 'Get segment membership time-series (total, entered, left)',
  },
  getAnalysisMetrics: {
    method: 'GET',
    path: '/environments/:environment_id/metrics/analysis',
    pathParams: c.type<ServicesApiGetAnalysisPathParams>(),
    query: c.type<ServicesApiGetAnalysisQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetAnalysisResponse>(),
    },
    summary: 'Get analysis metrics for campaigns, newsletters, and transactional messages',
  },
  getSegmentMetrics: {
    method: 'GET',
    path: '/environments/:environment_id/segments/:segment_id/metrics',
    pathParams: c.type<ServicesApiGetSegmentMetricsPathParams>(),
    responses: {
      200: c.type<ServicesApiGetSegmentMetricsResponse>(),
    },
    summary: 'Get people counts and activity metrics for a segment',
  },

  /**
   * Workspace Settings API
   */
  getWorkspaceSettings: {
    method: 'GET',
    path: '/environments/:environment_id/settings/workspace',
    pathParams: c.type<ServicesApiGetWorkspaceSettingsPathParams>(),
    responses: {
      200: c.type<ServicesApiGetWorkspaceSettingsResponse>(),
    },
    summary: 'Get workspace settings including metrics timezone',
  },

  /**
   * Recents API
   */
  getRecents: {
    method: 'GET',
    path: '/environments/:environment_id/recents',
    pathParams: c.type<ServicesApiGetRecentsPathParams>(),
    query: c.type<ServicesApiGetRecentsQueryParams>(),
    responses: {
      200: c.type<ServicesApiRecentResource[]>(),
    },
    summary: 'Get recently edited resources for the authenticated user',
  },

  getRecentlyShipped: {
    method: 'GET',
    path: '/environments/:environment_id/recents/shipped',
    pathParams: c.type<ServicesApiGetRecentlyShippedPathParams>(),
    query: c.type<ServicesApiGetRecentlyShippedQueryParams>(),
    responses: {
      200: c.type<ServicesApiShippedResource[]>(),
    },
    summary: 'Get recently launched resources across the workspace',
  },

  /**
   * Mail Servers API
   */
  getMailServers: {
    method: 'GET',
    path: '/environments/:environment_id/mail_servers',
    pathParams: c.type<ServicesApiGetMailServersPathParams>(),
    responses: {
      200: c.type<ServicesApiGetMailServersResponse>(),
    },
    summary: 'Get mail servers (custom SMTP) for a workspace',
  },
  createMailServer: {
    method: 'POST',
    path: '/environments/:environment_id/mail_servers',
    pathParams: c.type<ServicesApiCreateMailServerPathParams>(),
    body: c.type<ServicesApiCreateMailServerBody>(),
    responses: {
      200: c.type<ServicesApiCreateMailServerResponse>(),
    },
    summary: 'Create a mail server (custom SMTP) for a workspace',
  },
  deleteMailServer: {
    method: 'DELETE',
    path: '/environments/:environment_id/mail_servers/:id',
    pathParams: c.type<ServicesApiDeleteMailServerPathParams>(),
    body: c.type<Record<string, never>>(),
    responses: {
      200: c.type<void>(),
    },
    summary: 'Delete a mail server',
  },
  getLayouts: {
    method: 'GET',
    path: '/environments/:environment_id/layouts',
    pathParams: c.type<ServicesApiGetLayoutsPathParams>(),
    responses: {
      200: c.type<ServicesApiGetLayoutsResponse>(),
    },
    summary: 'Get layouts for a workspace',
  },
  getDefaultFromIdentity: {
    method: 'GET',
    path: '/environments/:environment_id/default_from_identity',
    pathParams: c.type<ServicesApiGetDefaultFromIdentityPathParams>(),
    responses: {
      200: c.type<ServicesApiGetDefaultFromIdentityResponse>(),
    },
    summary: 'Get default from identity for a workspace',
  },
  createNewsletter: {
    method: 'POST',
    path: '/environments/:environment_id/newsletters',
    pathParams: c.type<ServicesApiCreateNewsletterPathParams>(),
    body: c.type<ServicesApiCreateNewsletterBody>(),
    responses: {
      200: c.type<ServicesApiCreateNewsletterResponse>(),
    },
    summary: 'Create a newsletter (broadcast)',
  },
  updateNewsletter: {
    method: 'PUT',
    path: '/environments/:environment_id/newsletters/:id',
    pathParams: c.type<ServicesApiUpdateNewsletterPathParams>(),
    body: c.type<ServicesApiUpdateNewsletterBody>(),
    responses: {
      200: c.type<ServicesApiUpdateNewsletterResponse>(),
    },
    summary: 'Update a newsletter (broadcast)',
  },
  getTemplateById: {
    method: 'GET',
    path: '/environments/:environment_id/templates/:template_id',
    pathParams: c.type<ServicesApiGetTemplateByIdPathParams>(),
    responses: {
      200: c.type<{ template: ServicesApiTemplate }>(),
    },
    summary: 'Get a single template by ID',
  },

  updateTemplate: {
    method: 'PUT',
    path: '/environments/:environment_id/templates/:template_id',
    pathParams: c.type<ServicesApiUpdateTemplatePathParams>(),
    body: c.type<ServicesApiUpdateTemplateBody>(),
    responses: {
      200: c.type<ServicesApiUpdateTemplateResponse>(),
    },
    summary: 'Update a template',
  },

  previewEmail: {
    method: 'POST',
    path: '/environments/:environment_id/previews/email',
    pathParams: c.type<{ environment_id: number }>(),
    body: c.type<ServicesApiPreviewEmailBody>(),
    responses: {
      200: c.type<ServicesApiPreviewEmailResponse>(),
    },
    summary: 'Render an email template preview',
  },

  /**
   * Delivery Filters API
   * Returns messages (campaign actions) with output_id for condition display
   */
  getDeliveryFilters: {
    method: 'GET',
    path: '/environments/:environment_id/deliveries/filters',
    pathParams: c.type<ServicesApiGetDeliveryFiltersPathParams>(),
    query: c.type<ServicesApiGetDeliveryFiltersQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetDeliveryFiltersResponse>(),
    },
    summary: 'Get delivery filters including messages with output_id for condition display',
  },
  getFeatureFlags: {
    method: 'GET',
    path: '/accounts/:accountId/feature_flags',
    pathParams: c.type<{ accountId: number }>(),
    responses: {
      200: c.type<{ featureFlags: Record<string, boolean> }>(),
    },
    summary: 'Get feature flags for an account',
  },
  updateFeatureFlags: {
    method: 'PUT',
    path: '/accounts/:accountId/feature_flags',
    pathParams: c.type<{ accountId: number }>(),
    body: c.type<Record<string, boolean>>(),
    responses: {
      200: c.type<Record<string, boolean>>(),
    },
    summary: 'Update feature flags for an account',
  },
  deprioritizeBackfills: {
    method: 'POST',
    path: '/environments/:environment_id/backfills/:backfill_id/deprioritize',
    pathParams: c.type<{ environment_id: number; backfill_id: number }>(),
    body: c.type<{ mode?: 'next' | 'last' }>(),
    responses: {
      204: c.type<void>(),
    },
    summary: 'Deprioritize a backfill',
  },
  cancelSegmentBackfill: {
    method: 'POST',
    path: '/environments/:environment_id/segments/:segment_id/cancel',
    pathParams: c.type<{ environment_id: number; segment_id: number }>(),
    body: c.type<void>(),
    responses: {
      204: c.type<void>(),
    },
    summary: 'Cancel a segment backfill',
  },

  /**
   * Dev Tools - Populate Workspace
   */
  populateWorkspace: {
    method: 'POST',
    path: '/environments/:environment_id/populate',
    pathParams: c.type<{ environment_id: number }>(),
    body: c.type<PopulateWorkspaceBody>(),
    responses: {
      200: c.type<PopulateWorkspaceResponse>(),
    },
    summary: 'Populate workspace with test people and events',
  },

  /**
   * People — list customers in the workspace
   * GET /v1/environments/:workspaceId/customers
   */
  getPeople: {
    method: 'GET',
    path: '/environments/:workspaceId/customers',
    pathParams: c.type<{ workspaceId: number }>(),
    query: c.type<ServicesApiGetPeopleQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetPeopleResponse>(),
    },
    summary: 'List customers in the workspace',
  },

  /**
   * People — create a customer in the workspace.
   * Mirrors the Journeys UI POST /customers flow used by the "Add person" page.
   */
  createCustomer: {
    method: 'POST',
    path: '/environments/:workspaceId/customers',
    pathParams: c.type<{ workspaceId: number }>(),
    body: c.type<{ customer: { attributes: Record<string, string> } }>(),
    responses: {
      // Backend (services/ui_api/customers/router.go) registers this with
      // BodyHandlerNoContent + WithStatus(202). Empty body — the caller must
      // know the customer's external identifier (e.g. email) to follow up.
      202: c.type<void>(),
    },
    summary: 'Create a person in the workspace',
  },

  /**
   * Test users — send the double-opt-in email so a person can receive Builder-plan messages.
   * Mirrors the Journeys UI "Mark as test user" action.
   */
  markCustomerAsTestUser: {
    method: 'PUT',
    path: '/environments/:workspaceId/customers/:customerId/double_opt_in',
    pathParams: c.type<{ workspaceId: number; customerId: string }>(),
    body: c.type<Record<string, never>>(),
    responses: {
      // Backend registers this with HandlerNoContent + WithStatus(202).
      202: c.type<void>(),
    },
    summary: 'Send the double-opt-in email to mark a customer as a test user',
  },

  /**
   * Test users — count how many test users (people flagged with `cio_test_user`)
   * exist in the workspace. Used to compute remaining spots in the Builder-plan
   * onboarding step, so the count reflects test users added in other sessions or
   * by other admins.
   */
  getTestUserCount: {
    method: 'GET',
    path: '/environments/:workspaceId/test_users/count',
    pathParams: c.type<{ workspaceId: number }>(),
    responses: {
      200: c.type<{ count: number; limit: number }>(),
    },
    summary: 'Get count of test users (and the plan limit) for the workspace',
  },

  /**
   * List test users (cio_test_user=true) for the workspace, including
   * profiles still pending double-opt-in. Used by the Builders onboarding
   * flow to rehydrate the "added test users" tag list across page refreshes
   * — the count alone can't drive the UI because it doesn't carry the
   * underlying email addresses. Capped at 10 server-side; total reflects
   * the full match count.
   */
  getTestUsers: {
    method: 'GET',
    path: '/environments/:workspaceId/test_users',
    pathParams: c.type<{ workspaceId: number }>(),
    query: c.type<{ verified?: boolean }>(),
    responses: {
      200: c.type<{
        test_users: { internal_id: string; identifiers?: { email?: string | null } | null }[];
        total: number;
      }>(),
    },
    summary: 'List test users in the workspace',
  },

  /**
   * Privacy suppression - Get count of suppressed customers (Journeys_People_View)
   * GET /v1/environments/:workspaceId/customers_suppression_count
   */
  getSuppressionCount: {
    method: 'GET',
    path: '/environments/:workspaceId/customers_suppression_count',
    pathParams: c.type<{ workspaceId: number }>(),
    responses: {
      200: c.type<{ count: number }>(),
    },
    summary: 'Get count of privacy-suppressed customers in the workspace',
  },

  /**
   * Privacy suppression - Unsuppress customer identifiers
   * POST /v1/environments/:workspaceId/customers/unsuppress
   * At least one of customer_ids, internal_ids, or emails must be non-empty.
   */
  unsuppressCustomers: {
    method: 'POST',
    path: '/environments/:workspaceId/customers/unsuppress',
    pathParams: c.type<{ workspaceId: number }>(),
    body: c.type<{
      customer_ids?: string[];
      internal_ids?: string[];
      emails?: string[];
    }>(),
    responses: {
      200: c.type<{
        matched: string[];
        not_suppressed: string[];
        not_found: string[];
        message: string;
      }>(),
    },
    summary: 'Unsuppress customer identifiers so they can be added to the workspace again',
  },

  /**
   * Environment Health API
   */
  getEnvHealth: {
    method: 'GET',
    path: '/environments/:environment_id/health',
    pathParams: c.type<ServicesApiGetEnvHealthPathParams>(),
    query: c.type<ServicesApiGetEnvHealthQueryParams>(),
    responses: {
      200: c.type<ServicesApiEnvHealthResponse>(),
    },
    summary: 'Get environment health status including queue processing and backfill tasks',
  },
  getEnvHealthTopEvents: {
    method: 'GET',
    path: '/environments/:environment_id/health/top_events',
    pathParams: c.type<ServicesApiGetEnvHealthPathParams>(),
    query: c.type<ServicesApiGetEnvHealthQueryParams>(),
    responses: {
      200: c.type<ServicesApiEnvHealthTopEventsResponse>(),
    },
    summary: 'Get top event names for environment health',
  },
  getEnvHealthSegments: {
    method: 'GET',
    path: '/environments/:environment_id/health/segments',
    pathParams: c.type<ServicesApiGetEnvHealthPathParams>(),
    query: c.type<ServicesApiGetEnvHealthQueryParams>(),
    responses: {
      200: c.type<ServicesApiEnvHealthSegmentsResponse>(),
    },
    summary: 'Get segment usage stats for environment health',
  },
  getEnvHealthObjects: {
    method: 'GET',
    path: '/environments/:environment_id/health/objects',
    pathParams: c.type<ServicesApiGetEnvHealthPathParams>(),
    query: c.type<ServicesApiGetEnvHealthQueryParams>(),
    responses: {
      200: c.type<ServicesApiEnvHealthObjectsResponse>(),
    },
    summary: 'Get custom object stats for environment health',
  },
  getEnvHealthPipelines: {
    method: 'GET',
    path: '/environments/:environment_id/health/pipelines',
    pathParams: c.type<ServicesApiGetEnvHealthPathParams>(),
    query: c.type<ServicesApiGetEnvHealthQueryParams>(),
    responses: {
      200: c.type<ServicesApiEnvHealthPipelinesResponse>(),
    },
    summary: 'Get pipeline integration usage and connection health for environment',
  },
  getAttributesTotals: {
    method: 'GET',
    path: '/environments/:environment_id/attributes_totals',
    pathParams: c.type<ServicesApiGetTotalsPathParams>(),
    responses: {
      200: c.type<ServicesApiTotalsResponse>(),
    },
    summary: 'Get attribute counts (all and in-use)',
  },
  getEventNamesTotals: {
    method: 'GET',
    path: '/environments/:environment_id/event_names_totals',
    pathParams: c.type<ServicesApiGetTotalsPathParams>(),
    responses: {
      200: c.type<ServicesApiTotalsResponse>(),
    },
    summary: 'Get event name counts (all and in-use)',
  },

  /**
   * Attributes List API (paginated, with search/filter)
   */
  getAttributes: {
    method: 'GET',
    path: '/environments/:environment_id/attributes',
    pathParams: c.type<{ environment_id: number }>(),
    query: c.type<ServicesApiGetAttributesQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetAttributesResponse>(),
    },
    summary: 'Get paginated list of attribute names with metadata',
  },

  /**
   * Event Names List API (paginated, with search/filter)
   */
  getEventNames: {
    method: 'GET',
    path: '/environments/:environment_id/event_names',
    pathParams: c.type<{ environment_id: number }>(),
    query: c.type<ServicesApiGetEventNamesQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetEventNamesResponse>(),
    },
    summary: 'Get paginated list of event names with metadata',
  },

  /**
   * Object Types API
   */
  getObjectTypes: {
    method: 'GET',
    path: '/environments/:environment_id/object_types',
    pathParams: c.type<{ environment_id: number }>(),
    responses: {
      200: c.type<ServicesApiGetObjectTypesResponse>(),
    },
    summary: 'Get all object types for a workspace',
  },

  /**
   * Subscription Topics API
   */
  getSubscriptionTopics: {
    method: 'GET',
    path: '/environments/:environment_id/subscription_topics',
    pathParams: c.type<{ environment_id: number }>(),
    responses: {
      200: c.type<ServicesApiGetSubscriptionTopicsResponse>(),
    },
    summary: 'Get all subscription topics for a workspace',
  },

  getSubscriptionTopicById: {
    method: 'GET',
    path: '/environments/:environment_id/subscription_topics/:topic_id',
    pathParams: c.type<{ environment_id: number; topic_id: string }>(),
    responses: {
      200: c.type<ServicesApiSubscriptionTopicResponse>(),
    },
    summary: 'Get a subscription topic by ID',
  },

  createSubscriptionTopic: {
    method: 'POST',
    path: '/environments/:environment_id/subscription_topics',
    pathParams: c.type<{ environment_id: number }>(),
    body: c.type<ServicesApiSubscriptionTopicWriteBody>(),
    responses: {
      200: c.type<ServicesApiSubscriptionTopicResponse>(),
    },
    summary: 'Create a subscription topic',
  },

  updateSubscriptionTopic: {
    method: 'PUT',
    path: '/environments/:environment_id/subscription_topics/:topic_id',
    pathParams: c.type<{ environment_id: number; topic_id: string }>(),
    body: c.type<ServicesApiSubscriptionTopicWriteBody>(),
    responses: {
      200: c.type<ServicesApiSubscriptionTopicResponse>(),
    },
    summary: 'Update a subscription topic',
  },

  deleteSubscriptionTopic: {
    method: 'DELETE',
    path: '/environments/:environment_id/subscription_topics/:topic_id',
    pathParams: c.type<{ environment_id: number; topic_id: string }>(),
    body: c.type<void>(),
    responses: {
      204: c.type<void>(),
    },
    summary: 'Delete a subscription topic',
  },

  /**
   * Custom Forms API
   */
  getCustomForms: {
    method: 'GET',
    path: '/environments/:environment_id/custom_forms',
    pathParams: c.type<{ environment_id: number }>(),
    responses: {
      200: c.type<ServicesApiGetCustomFormsResponse>(),
    },
    summary: 'Get all custom forms for a workspace',
  },

  /**
   * Facebook Lead Ads Forms API
   */
  getFacebookLeadAdsForms: {
    method: 'GET',
    path: '/environments/:environment_id/facebook_lead_ads_forms',
    pathParams: c.type<{ environment_id: number }>(),
    responses: {
      200: c.type<ServicesApiGetFacebookLeadAdsFormsResponse>(),
    },
    summary: 'Get all Facebook Lead Ads forms for a workspace',
  },

  getEnvHealthCampaigns: {
    method: 'GET',
    path: '/environments/:environment_id/health/campaigns',
    pathParams: c.type<ServicesApiGetEnvHealthPathParams>(),
    query: c.type<ServicesApiGetEnvHealthQueryParams>(),
    responses: {
      200: c.type<ServicesApiEnvHealthCampaignsResponse>(),
    },
    summary: 'Get stale campaign stats for environment health',
  },
  getEnvHealthApiTriggeredBroadcasts: {
    method: 'GET',
    path: '/environments/:environment_id/health/api_triggered_broadcasts',
    pathParams: c.type<ServicesApiGetEnvHealthPathParams>(),
    query: c.type<ServicesApiGetEnvHealthQueryParams>(),
    responses: {
      200: c.type<ServicesApiEnvHealthApiTriggeredBroadcastsResponse>(),
    },
    summary: 'Get API-triggered broadcast health for environment',
  },
  getEnvHealthPush: {
    method: 'GET',
    path: '/environments/:environment_id/health/push',
    pathParams: c.type<ServicesApiGetEnvHealthPathParams>(),
    query: c.type<ServicesApiGetEnvHealthQueryParams>(),
    responses: {
      200: c.type<ServicesApiEnvHealthPushResponse>(),
    },
    summary: 'Get push notification health for environment',
  },
  getEnvHealthDelayHistory: {
    method: 'GET',
    path: '/environments/:environment_id/health/delay_history',
    pathParams: c.type<ServicesApiGetEnvHealthPathParams>(),
    query: c.type<ServicesApiGetEnvHealthQueryParams>(),
    responses: {
      200: c.type<ServicesApiEnvHealthDelayHistoryResponse>(),
    },
    summary: 'Get 24h delay history for environment health',
  },
  getEnvHealthCheck: {
    method: 'GET',
    path: '/environments/:environment_id/health/check',
    pathParams: c.type<ServicesApiGetEnvHealthPathParams>(),
    responses: {
      200: c.type<ServicesApiEnvHealthCheckResponse>(),
    },
    summary: 'Lightweight health check; returns whether heavy /health endpoint should be polled',
  },

  /**
   * Domains API
   * GET /v1/environments/:workspaceId/domains
   */
  getDomains: {
    method: 'GET',
    path: '/environments/:workspaceId/domains',
    pathParams: c.type<{ workspaceId: number }>(),
    responses: {
      200: c.type<ServicesApiGetDomainsResponse>(),
    },
    summary: 'Get all domains for a workspace',
  },
  getDomainById: {
    method: 'GET',
    path: '/environments/:workspaceId/domains/:domainId',
    pathParams: c.type<{ workspaceId: number; domainId: string }>(),
    responses: {
      200: c.type<ServicesApiDomainResponse>(),
    },
    summary: 'Get a single sending domain (and its paired auto-TLS row)',
  },
  createDomain: {
    method: 'POST',
    path: '/environments/:workspaceId/domains',
    pathParams: c.type<{ workspaceId: number }>(),
    body: c.type<{ domain: { domain: string }; auto_tls?: boolean }>(),
    responses: {
      200: c.type<ServicesApiDomainCreateResponse>(),
    },
    summary: 'Create a new sending domain',
  },
  getDomainEntriToken: {
    method: 'GET',
    path: '/environments/:workspaceId/domains/:domainId/entri_token',
    pathParams: c.type<{ workspaceId: number; domainId: string }>(),
    query: c.type<{ flow: 'domain_auth' | 'link_tracking' }>(),
    responses: {
      200: c.type<{ token: string; application_id: string; user_id: string }>(),
    },
    summary: 'Get Entri token for DNS setup',
  },
  verifyDomain: {
    method: 'POST',
    path: '/environments/:workspaceId/domains/:domainId/verify',
    pathParams: c.type<{ workspaceId: number; domainId: string }>(),
    body: c.type<void>(),
    responses: {
      200: c.type<{
        domain: ServicesApiDomain;
        errors?: Record<string, string[]>;
        warnings?: Record<string, string[]>;
      }>(),
    },
    summary: 'Verify sending domain DNS records',
  },

  updateDomain: {
    method: 'PUT',
    path: '/environments/:workspaceId/domains/:domainId',
    pathParams: c.type<{ workspaceId: number; domainId: string }>(),
    body: c.type<ServicesApiUpdateDomainBody>(),
    responses: {
      200: c.type<ServicesApiDomainUpdateResponse>(),
    },
    summary: 'Update a sending domain (currently only `cname` is mutable)',
  },

  deleteDomain: {
    method: 'DELETE',
    path: '/environments/:workspaceId/domains/:domainId',
    pathParams: c.type<{ workspaceId: number; domainId: string }>(),
    body: c.type<void>(),
    responses: {
      204: c.type<void>(),
    },
    summary: 'Delete a sending domain',
  },

  requestIpPool: {
    method: 'POST',
    path: '/environments/:workspaceId/domains/:domainId/ip_pool',
    pathParams: c.type<{ workspaceId: number; domainId: string }>(),
    body: c.type<ServicesApiRequestIpPoolBody>(),
    responses: {
      200: c.type<ServicesApiDomainResponse>(),
    },
    summary: 'Request a new IP pool assignment for a sending domain (modelApiAction equivalent)',
  },

  enableAutoTls: {
    method: 'POST',
    path: '/environments/:workspaceId/domains/:domainId/enable_auto_tls',
    pathParams: c.type<{ workspaceId: number; domainId: string }>(),
    body: c.type<void>(),
    responses: {
      200: c.type<ServicesApiDomainResponse>(),
    },
    summary: 'Enable auto-TLS for a sending domain (modelApiAction equivalent)',
  },

  /**
   * Auto-TLS domains API.
   *
   * The list and other auto-TLS-domain endpoints aren't exposed yet — auto-TLS
   * rows arrive sideloaded from `getDomains`. This single-record endpoint
   * gives `useLiveQuery((q) => q.from({a: autoTlsDomains}).where(({a}) => eq(a.id, X)))`
   * Ember Data `findRecord` parity for the rare case where a row needs to be
   * fetched standalone (e.g. a deep link to an auto-TLS detail page).
   */
  getAutoTlsDomainById: {
    method: 'GET',
    path: '/environments/:environment_id/auto_tls_domains/:auto_tls_domain_id',
    pathParams: c.type<{ environment_id: number; auto_tls_domain_id: string }>(),
    responses: {
      200: c.type<ServicesApiAutoTLSDomainResponse>(),
    },
    summary: 'Get a single auto-TLS domain',
  },
  createIdentity: {
    method: 'POST',
    path: '/environments/:workspaceId/identities',
    pathParams: c.type<{ workspaceId: number }>(),
    body: c.type<{
      identity: { name: string; email: string; template_type: 'email' | 'twilio' };
    }>(),
    responses: {
      200: c.type<{ identity: { id: string; address: string; email: string } }>(),
    },
    summary: 'Create a sending identity (from address)',
  },
  createExtApiKey: {
    method: 'POST',
    path: '/environments/:workspaceId/ext_api_keys',
    pathParams: c.type<{ workspaceId: number }>(),
    body: c.type<{ ext_api_key: { name: string } }>(),
    responses: {
      200: c.type<{
        ext_api_key: {
          id: string;
          name: string;
          api_key: string;
          hint: string;
          created_at: number;
        };
      }>(),
    },
    summary: 'Create an extension API key',
  },

  /**
   * Releases API
   * GET /v1/public_releases/summary
   */
  getReleaseSummary: {
    method: 'GET',
    path: '/public_releases/summary',
    responses: {
      200: c.type<ServicesApiReleaseSummaryResponse>(),
    },
    summary: 'Get the current release summary',
  },

  /**
   * Service Accounts API
   */
  listServiceAccounts: {
    method: 'GET',
    path: '/accounts/:accountId/service_accounts',
    pathParams: c.type<{ accountId: number }>(),
    responses: {
      200: c.type<ServicesApiServiceAccount[]>(),
    },
    summary: 'List all service accounts for an account',
  },
  createServiceAccount: {
    method: 'POST',
    path: '/accounts/:accountId/service_accounts',
    pathParams: c.type<{ accountId: number }>(),
    body: c.type<{ name: string; description: string }>(),
    responses: {
      200: c.type<ServicesApiServiceAccount>(),
    },
    summary: 'Create a new service account',
  },
  updateServiceAccount: {
    method: 'PUT',
    path: '/accounts/:accountId/service_accounts/:serviceAccountId',
    pathParams: c.type<ServicesApiServiceAccountPathParams>(),
    body: c.type<{ name: string; description: string }>(),
    responses: {
      200: c.type<ServicesApiServiceAccount>(),
    },
    summary: 'Update a service account',
  },
  deleteServiceAccount: {
    method: 'DELETE',
    path: '/accounts/:accountId/service_accounts/:serviceAccountId',
    pathParams: c.type<ServicesApiServiceAccountPathParams>(),
    body: c.type<void>(),
    responses: {
      200: c.type<{ ok: boolean }>(),
    },
    summary: 'Delete a service account and revoke all tokens',
  },
  listServiceAccountTokens: {
    method: 'GET',
    path: '/accounts/:accountId/service_accounts/:serviceAccountId/tokens',
    pathParams: c.type<ServicesApiServiceAccountPathParams>(),
    responses: {
      200: c.type<ServicesApiServiceAccountToken[]>(),
    },
    summary: 'List tokens for a service account',
  },
  createServiceAccountToken: {
    method: 'POST',
    path: '/accounts/:accountId/service_accounts/:serviceAccountId/tokens',
    pathParams: c.type<ServicesApiServiceAccountPathParams>(),
    body: c.type<{ name: string; expires_at?: string; read_only?: boolean }>(),
    responses: {
      200: c.type<ServicesApiCreateTokenResponse>(),
    },
    summary: 'Create a new token for a service account',
  },
  revokeServiceAccountToken: {
    method: 'DELETE',
    path: '/accounts/:accountId/service_accounts/:serviceAccountId/tokens/:tokenId',
    pathParams: c.type<ServicesApiTokenPathParams>(),
    body: c.type<void>(),
    responses: {
      200: c.type<{ ok: boolean }>(),
    },
    summary: 'Revoke a service account token',
  },

  /**
   * Webhook Signing Secret API
   */
  getSigningSecret: {
    method: 'GET',
    path: '/environments/:environmentId/signing_secret',
    pathParams: c.type<{ environmentId: number }>(),
    responses: {
      200: c.type<ServicesApiSigningSecretResponse>(),
    },
    summary: 'Get the webhook signing secret for a workspace',
  },
  regenerateSigningSecret: {
    method: 'PUT',
    path: '/environments/:environmentId/signing_secret',
    pathParams: c.type<{ environmentId: number }>(),
    body: c.type<void>(),
    responses: {
      200: c.type<ServicesApiSigningSecretResponse>(),
    },
    summary: 'Regenerate the webhook signing secret',
  },

  /**
   * Assets API
   */
  listAssets: {
    method: 'GET',
    path: '/environments/:environment_id/assets',
    pathParams: c.type<ServicesApiListAssetsPathParams>(),
    query: c.type<ServicesApiListAssetsQueryParams>(),
    responses: {
      200: c.type<ServicesApiListAssetsResponse>(),
    },
    summary: 'List assets for a workspace with pagination, search, sorting, and folder filtering',
  },
  createAsset: {
    method: 'POST',
    path: '/environments/:environment_id/assets',
    pathParams: c.type<ServicesApiCreateAssetPathParams>(),
    body: c.type<ServicesApiCreateAssetBody>(),
    responses: {
      200: c.type<ServicesApiCreateAssetResponse>(),
    },
    summary: 'Create a new asset or folder',
  },
  moveAssets: {
    method: 'PUT',
    path: '/environments/:environment_id/assets',
    pathParams: c.type<ServicesApiMoveAssetsPathParams>(),
    body: c.type<ServicesApiMoveAssetsBody>(),
    responses: {
      200: c.type<Record<string, unknown>>(),
    },
    summary: 'Move assets to a different folder',
  },
  deleteAssets: {
    method: 'DELETE',
    path: '/environments/:environment_id/assets',
    pathParams: c.type<ServicesApiDeleteAssetsPathParams>(),
    body: c.type<ServicesApiDeleteAssetsBody>(),
    responses: {
      200: c.type<Record<string, unknown>>(),
    },
    summary: 'Delete one or more assets',
  },
});

const { api } = createApi(contract, {
  baseUrl: '/v1',
});

const augmentedApi = {
  ...api,
  getAccountById: async (...params: Parameters<typeof api.getAccountById>): Promise<ServicesApiAccountResponse> => {
    const accountId = params[0].params.accountId;
    if (accountId != null) {
      const cachedAccount = crossAppCache.getValue(`/accounts/${accountId}`);
      if (cachedAccount != null) {
        return cachedAccount as ServicesApiAccountResponse;
      }
    }

    const response = await api.getAccountById(...params);

    if (response?.account?.id != null) {
      crossAppCache.setValue({
        key: `/accounts/${accountId}`,
        value: response,
        ttl: 10000,
        cacheOnlyOnce: true,
      });
    }

    return response;
  },
  getAccountByEnvironmentId: async (
    ...params: Parameters<typeof api.getAccountByEnvironmentId>
  ): Promise<ServicesApiAccountResponse> => {
    const environmentId = params[0].params.environmentId;

    if (environmentId != null) {
      const cachedAccount = crossAppCache.getValue(`/account_by_environment/${environmentId}`);
      if (cachedAccount != null) {
        return cachedAccount as ServicesApiAccountResponse;
      }
    }

    const response = await api.getAccountByEnvironmentId(...params);

    if (response?.account?.id != null) {
      crossAppCache.setValue({
        key: `/account_by_environment/${environmentId}`,
        value: response,
        ttl: 10000,
        cacheOnlyOnce: true,
      });
      crossAppCache.setValue({
        key: `/accounts/${response.account.id}`,
        value: response,
        ttl: 10000,
        cacheOnlyOnce: true,
      });
    }
    return response;
  },
  getAccount: async (...params: Parameters<typeof api.getAccount>): Promise<ServicesApiAccountResponse> => {
    const cachedAccount = crossAppCache.getValue(`/accounts/current`);
    if (cachedAccount != null) {
      return cachedAccount as ServicesApiAccountResponse;
    }

    const response = await api.getAccount(...params);

    if (response?.account?.id != null) {
      crossAppCache.setValue({
        key: `/accounts/current`,
        value: response,
        ttl: 10000,
        cacheOnlyOnce: true,
      });

      crossAppCache.setValue({
        key: `/accounts/${response.account.id}`,
        value: response,
        ttl: 10000,
        cacheOnlyOnce: true,
      });
    }

    return response;
  },
};

// Create hooks using the augmented API to ensure caching behavior is preserved
// This lightweight function creates only the hooks without reinitializing the entire client
const { useQuery, useMutation, useInfiniteQuery, getQueryOptions } = createQueryHooks(contract, augmentedApi);

export {
  augmentedApi as api,
  useQuery as useServicesQuery,
  useMutation as useServicesMutation,
  useInfiniteQuery as useServicesInfiniteQuery,
  getQueryOptions as getServicesQueryOptions,
};
